import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import MobilePilotLogbook from './logbook/MobilePilotLogbook';
import { MEDIA_QUERIES, useMediaQuery } from './responsive';
import { supabase } from './supabaseClient';
import { useSession } from './SessionContext';
import WingitLogo from './components/WingitLogo';
import LogbookStatisticsCharts from './components/LogbookStatisticsCharts';
import { fetchOfficialTechLogsForBookings, indexOfficialTechLogs, saveOfficialTechLogEntry, saveOfficialTechLogEntryAndCompleteBooking } from './officialTechLog';
import { getResolvedPostFlightStatus, hasPersistentPilotLogbookCompletionMismatch, hasSavedPilotLogbookEntry, isGenuineSavedFlightEntry } from './booking/activeFlightSelection';

const buttonStyle = {
  background: '#1ecfff',
  color: '#181a2a',
  border: 'none',
  borderRadius: 8,
  padding: '0.5rem 1.2rem',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(30,207,255,0.10)'
};

const spreadsheetInputStyle = {
  width: '100%',
  height: '100%',
  minWidth: 0,
  padding: '0.03rem 0.08rem',
  borderRadius: 0,
  border: 'none',
  background: 'transparent',
  boxShadow: 'none',
  outline: 'none',
  color: '#151515',
  textTransform: 'uppercase',
  fontSize: 'inherit',
  fontFamily: 'inherit',
  lineHeight: 'inherit',
  boxSizing: 'border-box',
};

export const LOGBOOK_TIME_FORMAT_HOURS_MINUTES = 'hours-minutes';
export const LOGBOOK_TIME_FORMAT_DECIMAL_HOURS = 'decimal-hours';
export const LOGBOOK_DECIMAL_PLACES = 1;
export const ALLOCATION_ELIGIBLE_FIELDS = [
  'seDayDual',
  'seDayPic',
  'seNightDual',
  'seNightPic',
  'meDayDual',
  'meDayPic',
  'meDayCopilot',
  'meDayPiUs',
  'meNightDual',
  'meNightPic',
  'meNightCopilot',
  'meNightPiUs',
];

function normalizePilotTimeFormat(value) {
  return value === LOGBOOK_TIME_FORMAT_HOURS_MINUTES
    ? LOGBOOK_TIME_FORMAT_HOURS_MINUTES
    : LOGBOOK_TIME_FORMAT_DECIMAL_HOURS;
}

function parseHoursMinutesStringToMinutes(value) {
  const text = String(value || '').trim();
  if (!text) return null;

  const match = text.match(/^(\d{1,3}):(\d{2})$/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes < 0 || minutes > 59) {
    return null;
  }

  return (hours * 60) + minutes;
}

function formatMinutesAsHoursMinutes(value) {
  const totalMinutes = Math.max(0, Math.round(Number(value) || 0));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}`;
}

function minutesToRoundedDecimalHours(value) {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) return 0;
  return Number((minutes / 60).toFixed(LOGBOOK_DECIMAL_PLACES));
}

function parseHourValueToNumber(value) {
  const text = String(value || '').trim();
  if (!text) return 0;

  const hoursMinutes = parseHoursMinutesStringToMinutes(text);
  if (Number.isFinite(hoursMinutes)) {
    return hoursMinutes / 60;
  }

  const number = Number(text.replace(',', '.'));
  return Number.isFinite(number) ? number : 0;
}

function formatHourNumberForDisplay(value, timeFormat) {
  const normalizedFormat = normalizePilotTimeFormat(timeFormat);
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return '';

  if (normalizedFormat === LOGBOOK_TIME_FORMAT_HOURS_MINUTES) {
    return formatMinutesAsHoursMinutes(number * 60);
  }

  return number.toFixed(LOGBOOK_DECIMAL_PLACES);
}

export function formatHourValueForDisplay(value, timeFormat) {
  const text = String(value || '').trim();
  if (!text) return '';

  const hoursMinutes = parseHoursMinutesStringToMinutes(text);
  if (Number.isFinite(hoursMinutes)) {
    if (normalizePilotTimeFormat(timeFormat) === LOGBOOK_TIME_FORMAT_HOURS_MINUTES) {
      return formatMinutesAsHoursMinutes(hoursMinutes);
    }
    return minutesToRoundedDecimalHours(hoursMinutes).toFixed(LOGBOOK_DECIMAL_PLACES);
  }

  return formatHourNumberForDisplay(parseHourValueToNumber(text), timeFormat);
}

export function normalizeHourValueForSave(value) {
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();
  if (!trimmed) return '';

  const hoursMinutes =
    parseHoursMinutesStringToMinutes(trimmed);

  if (Number.isFinite(hoursMinutes)) {
    return formatMinutesAsHoursMinutes(hoursMinutes);
  }

  let formatted = trimmed.replace(',', '.');

  if (formatted.startsWith('.')) {
    formatted = `0${formatted}`;
  }

  if (/^\d+$/.test(formatted)) {
    formatted = `${formatted}.0`;
  }

  return formatted;
}

function utcClockToMinutes(value) {
  const match = String(value || '').trim().match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  return (hours * 60) + minutes;
}

export function calculateElapsedMinutesFromUtc(depUtcValue, arrUtcValue) {
  const depUtc = normalizeUtcClockValue(depUtcValue);
  const arrUtc = normalizeUtcClockValue(arrUtcValue);

  if (!depUtc.ok || !arrUtc.ok || !depUtc.value || !arrUtc.value) {
    return null;
  }

  const depMinutes = utcClockToMinutes(depUtc.value);
  const arrMinutes = utcClockToMinutes(arrUtc.value);
  if (!Number.isFinite(depMinutes) || !Number.isFinite(arrMinutes)) {
    return null;
  }

  const adjustedArrival = arrMinutes < depMinutes ? arrMinutes + (24 * 60) : arrMinutes;
  return adjustedArrival - depMinutes;
}

export function formatDurationMinutesForDisplay(minutes, timeFormat) {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return '';

  if (normalizePilotTimeFormat(timeFormat) === LOGBOOK_TIME_FORMAT_HOURS_MINUTES) {
    return formatMinutesAsHoursMinutes(value);
  }

  return minutesToRoundedDecimalHours(value).toFixed(LOGBOOK_DECIMAL_PLACES);
}

export function applyAutoDurationToDraftRow(row) {
  if (!row) return row;
  const autoDurationMinutes = calculateElapsedMinutesFromUtc(row.depUtc, row.arrUtc);

  return {
    ...row,
    autoDurationMinutes: Number.isFinite(autoDurationMinutes) ? autoDurationMinutes : null,
  };
}

export function normalizeRowDateForDisplay(row) {
  if (!row) return row;
  return {
    ...row,
    date: formatDateToDdMmYyyy(row.date || ''),
  };
}

function shouldUseExactAutoDuration(row) {
  if (row?.isTrainingFlight) {
    return false;
  }

  const autoDurationMinutes =
    Number(row?.autoDurationMinutes);

  if (
    !Number.isFinite(autoDurationMinutes)
    || autoDurationMinutes <= 0
  ) {
    return false;
  }

  const explicitValue =
    String(row?.seDayPic || '').trim();

  // An automatic booking with no existing allocation should use
  // the exact duration calculated from its UTC times.
  if (!explicitValue) {
    return !row?.isManual;
  }

  // Manual flights must retain the value entered by the pilot.
  if (row?.isManual) {
    return false;
  }

  // H:MM is already an exact pilot-entered value.
  if (
    Number.isFinite(
      parseHoursMinutesStringToMinutes(explicitValue)
    )
  ) {
    return false;
  }

  // Older automatic entries may contain a rounded decimal value.
  // Use the exact UTC duration only when that decimal matches the
  // rounded version of the automatic duration.
  const explicitNumber =
    parseHourValueToNumber(explicitValue);

  const roundedAutoHours =
    minutesToRoundedDecimalHours(autoDurationMinutes);

  return (
    Number.isFinite(explicitNumber)
    && Math.abs(
      explicitNumber - roundedAutoHours
    ) < 0.0001
  );
}

function resolveSeDayPicValueForDisplay(row, timeFormat) {
  if (!row) return '';

  const autoDurationMinutes = Number(row.autoDurationMinutes);

  if (shouldUseExactAutoDuration(row)) {
    return formatDurationMinutesForDisplay(
      autoDurationMinutes,
      timeFormat
    );
  }

  const explicitValue = String(row.seDayPic || '').trim();

  if (explicitValue) {
    return formatHourValueForDisplay(
      explicitValue,
      timeFormat
    );
  }

  const fallbackFlightTime = Number(row.flightTime);

  if (
    Number.isFinite(fallbackFlightTime)
    && fallbackFlightTime > 0
  ) {
    return formatHourNumberForDisplay(
      fallbackFlightTime,
      timeFormat
    );
  }

  return '';
}

function resolveSeDayPicValueForSave(row) {
  if (shouldUseExactAutoDuration(row)) {
    return formatMinutesAsHoursMinutes(
      Number(row.autoDurationMinutes)
    );
  }

  const explicitValue =
    String(row?.seDayPic || '').trim();

  if (explicitValue) {
    return normalizeHourValueForSave(explicitValue);
  }

  return '';
}

function shouldUseExactAutoDualDuration(row) {
  const autoDurationMinutes =
    Number(row?.autoDurationMinutes);

  return Boolean(
    row?.isTrainingFlight &&
      row?.isDraft &&
      !row?.isManual &&
      !String(row?.seDayDual || '').trim() &&
      Number.isFinite(autoDurationMinutes) &&
      autoDurationMinutes > 0
  );
}

function resolveSeDayDualValueForDisplay(
  row,
  timeFormat
) {
  if (shouldUseExactAutoDualDuration(row)) {
    return formatDurationMinutesForDisplay(
      Number(row.autoDurationMinutes),
      timeFormat
    );
  }

  const explicitValue =
    String(row?.seDayDual || '').trim();

  return explicitValue
    ? formatHourValueForDisplay(
        explicitValue,
        timeFormat
      )
    : '';
}

function resolveSeDayDualValueForSave(row) {
  if (shouldUseExactAutoDualDuration(row)) {
    return formatMinutesAsHoursMinutes(
      Number(row.autoDurationMinutes)
    );
  }

  const explicitValue =
    String(row?.seDayDual || '').trim();

  return explicitValue
    ? normalizeHourValueForSave(explicitValue)
    : '';
}

export function parseHourTextToMinutes(value) {
  const text = String(value || '').trim();
  if (!text) return 0;

  const hoursMinutes = parseHoursMinutesStringToMinutes(text);
  if (Number.isFinite(hoursMinutes)) {
    return hoursMinutes;
  }

  const hoursNumber = parseHourValueToNumber(text);
  if (!Number.isFinite(hoursNumber) || hoursNumber <= 0) {
    return 0;
  }

  return Math.round(hoursNumber * 60);
}

function resolveAllocationFieldValueForCalculation(row = {}, field, timeFormat) {
  if (field === 'seDayDual') {
    return resolveSeDayDualValueForDisplay(
      row,
      timeFormat
    );
  }
  if (field === 'seDayPic') {
    return resolveSeDayPicValueForDisplay(row, timeFormat);
  }
  return String(row?.[field] || '').trim();
}

// Auto-derived P1/PIC time must use the exact duration in minutes for
// validation, not the tenths-rounded decimal-hours text used for display.
function resolveAllocationFieldMinutesForCalculation(row = {}, field, timeFormat) {
  if (
    field === 'seDayDual' &&
    shouldUseExactAutoDualDuration(row)
  ) {
    const autoDurationMinutes =
      Number(row?.autoDurationMinutes);
    return Number.isFinite(autoDurationMinutes) &&
      autoDurationMinutes > 0
      ? autoDurationMinutes
      : 0;
  }

  if (field === 'seDayPic' && shouldUseExactAutoDuration(row)) {
    const autoDurationMinutes = Number(row?.autoDurationMinutes);
    return Number.isFinite(autoDurationMinutes) && autoDurationMinutes > 0
      ? autoDurationMinutes
      : 0;
  }

  const fieldValue = resolveAllocationFieldValueForCalculation(row, field, timeFormat);
  return parseHourTextToMinutes(fieldValue);
}

export function getRowFlightDurationMinutes(row = {}) {
  const explicitDuration = Number(row?.autoDurationMinutes);
  if (Number.isFinite(explicitDuration) && explicitDuration > 0) {
    return explicitDuration;
  }

  const recalculated = calculateElapsedMinutesFromUtc(row?.depUtc, row?.arrUtc);
  if (!Number.isFinite(recalculated) || recalculated <= 0) {
    return null;
  }

  return recalculated;
}

// Half of one decimal-hours display unit (e.g. 0.05h = 3 min at 1 dp) - the maximum
// rounding uncertainty inherent in a manually-entered decimal-hours value. Not applied
// to HH:MM entries, which carry no such rounding ambiguity.
function getManualAllocationToleranceMinutes(timeFormat) {
  if (normalizePilotTimeFormat(timeFormat) !== LOGBOOK_TIME_FORMAT_DECIMAL_HOURS) {
    return 0;
  }

  return 60 / (2 * (10 ** LOGBOOK_DECIMAL_PLACES));
}

export function calculateAllocationSummaryForRow(row = {}, timeFormat, targetField = '') {
  const totalMinutes = getRowFlightDurationMinutes(row);
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) {
    return {
      hasDuration: false,
      totalMinutes: 0,
      allocatedMinutes: 0,
      remainingMinutes: 0,
      isOverAllocated: false,
    };
  }

  let allocatedMinutes = 0;

  ALLOCATION_ELIGIBLE_FIELDS.forEach((field) => {
    if (targetField && field === targetField) return;
    allocatedMinutes += resolveAllocationFieldMinutesForCalculation(row, field, timeFormat);
  });

  const remainingMinutes = totalMinutes - allocatedMinutes;
  const toleranceMinutes = getManualAllocationToleranceMinutes(timeFormat);

  return {
    hasDuration: true,
    totalMinutes,
    allocatedMinutes,
    remainingMinutes,
    isOverAllocated: remainingMinutes < -toleranceMinutes,
  };
}


export function getAutoFillRemainderValue(row = {}, timeFormat, targetField) {
  if (!ALLOCATION_ELIGIBLE_FIELDS.includes(targetField)) return '';

  const currentDisplayValue = resolveAllocationFieldValueForCalculation(row, targetField, timeFormat);
  if (String(currentDisplayValue || '').trim()) {
    return '';
  }

  const allocationSummary = calculateAllocationSummaryForRow(row, timeFormat, targetField);
  if (!allocationSummary.hasDuration || allocationSummary.isOverAllocated || allocationSummary.remainingMinutes <= 0) {
    return '';
  }

  return formatDurationMinutesForDisplay(allocationSummary.remainingMinutes, timeFormat);
}

function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

function isValidCalendarDateParts(day, month, year) {
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return false;
  if (year < 1000 || year > 9999) return false;
  if (month < 1 || month > 12) return false;

  const maxByMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const maxDay = maxByMonth[month - 1];
  return day >= 1 && day <= maxDay;
}

function formatDateParts(day, month, year) {
  const dd = String(day).padStart(2, '0');
  const mm = String(month).padStart(2, '0');
  const yyyy = String(year).padStart(4, '0');
  return {
    display: `${dd}/${mm}/${yyyy}`,
    storage: `${yyyy}-${mm}-${dd}`,
  };
}

export function normalizeLogbookDateInput(value) {
  const text = String(value || '').trim();
  if (!text) {
    return { ok: true, display: '', storage: '' };
  }

  const slashMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const day = Number(slashMatch[1]);
    const month = Number(slashMatch[2]);
    const year = Number(slashMatch[3]);
    if (!isValidCalendarDateParts(day, month, year)) {
      return { ok: false, error: 'Enter a real date as DD/MM/YYYY.' };
    }
    return { ok: true, ...formatDateParts(day, month, year) };
  }

  const isoOrTimestampMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  if (isoOrTimestampMatch) {
    const year = Number(isoOrTimestampMatch[1]);
    const month = Number(isoOrTimestampMatch[2]);
    const day = Number(isoOrTimestampMatch[3]);
    if (!isValidCalendarDateParts(day, month, year)) {
      return { ok: false, error: 'Enter a real date as DD/MM/YYYY.' };
    }
    return { ok: true, ...formatDateParts(day, month, year) };
  }

  return { ok: false, error: 'Enter date as DD/MM/YYYY.' };
}

function formatDateToDdMmYyyy(value) {
  const normalized = normalizeLogbookDateInput(value);
  return normalized.ok ? normalized.display : String(value || '').trim();
}

function formatDateDisplay(value) {
  const normalized = normalizeLogbookDateInput(value);
  if (!normalized.ok) return String(value || '').trim() || '-';
  return normalized.display || '-';
}

function formatLogbookPdfDate(value) {
  const normalized = normalizeLogbookDateInput(value);
  return normalized.ok ? normalized.display : String(value || '').trim();
}

function formatTimeWithColon(value) {
  const text = String(value || '').trim();
  if (!text) return '';

  if (/^\d{1,2}:\d{2}$/.test(text)) {
    const [hRaw, mRaw] = text.split(':');
    const hh = String(Number(hRaw) || 0).padStart(2, '0');
    const mm = String(Number(mRaw) || 0).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  const digits = text.replace(/\D/g, '');
  if (/^\d{3,4}$/.test(digits)) {
    const normalized = digits.padStart(4, '0');
    return `${normalized.slice(0, 2)}:${normalized.slice(2)}`;
  }

  return text;
}

function normalizeUtcClockValue(value) {
  const text = String(value || '').trim();
  if (!text) {
    return { ok: true, value: '' };
  }

  const colonMatch = text.match(/^(\d{1,2}):(\d{2})$/);
  let hours;
  let minutes;

  if (colonMatch) {
    hours = Number(colonMatch[1]);
    minutes = Number(colonMatch[2]);
  } else {
    const digits = text.replace(/\D/g, '');
    if (!/^\d{3,4}$/.test(digits)) {
      return { ok: false, error: 'Enter UTC as 1000 or 10:00.' };
    }

    const normalized = digits.padStart(4, '0');
    hours = Number(normalized.slice(0, 2));
    minutes = Number(normalized.slice(2));
  }

  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
    return { ok: false, error: 'Enter a valid UTC time.' };
  }

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return { ok: false, error: 'UTC time must be between 00:00 and 23:59.' };
  }

  return {
    ok: true,
    value: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
  };
}

function getUtcFieldErrorKey(rowId, field) {
  return `${String(rowId || '')}:${field}`;
}

function getRowChronologicalSortKey(row = {}) {
  const dateResult = normalizeLogbookDateInput(row?.date || '');
  const timeResult = normalizeUtcClockValue(row?.depUtc || '');

  if (!dateResult.ok || !dateResult.storage || !timeResult.ok || !timeResult.value) {
    return null;
  }

  const dateParts = dateResult.storage.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dateParts) return null;

  const year = Number(dateParts[1]);
  const month = Number(dateParts[2]);
  const day = Number(dateParts[3]);
  const dayEpoch = Math.floor(Date.UTC(year, month - 1, day) / 86400000);
  const depMinutes = utcClockToMinutes(timeResult.value);
  if (!Number.isFinite(dayEpoch) || !Number.isFinite(depMinutes)) {
    return null;
  }

  return (dayEpoch * 1440) + depMinutes;
}

export function sortPilotLogbookRowsForDisplay(rowsInput, dirtyRowIds = {}) {
  const rows = Array.isArray(rowsInput) ? rowsInput : [];
  if (rows.length <= 1) return rows;

  const pinnedBookingIds = new Set(
    Object.entries(dirtyRowIds || {})
      .filter(([, isDirty]) => Boolean(isDirty))
      .map(([bookingId]) => String(bookingId))
  );

  const indexedRows = rows.map((row, index) => ({
    row,
    index,
    key: getRowChronologicalSortKey(row),
    pinned: pinnedBookingIds.has(String(row?.bookingId || '')),
  }));

  const pinnedByIndex = new Map();
  indexedRows.forEach((item) => {
    if (item.pinned) {
      pinnedByIndex.set(item.index, item);
    }
  });

  const movableRows = indexedRows
    .filter((item) => !item.pinned)
    .sort((a, b) => {
      const aValid = Number.isFinite(a.key);
      const bValid = Number.isFinite(b.key);

      if (aValid && bValid) {
        if (a.key !== b.key) return a.key - b.key;
        return a.index - b.index;
      }

      if (aValid && !bValid) return -1;
      if (!aValid && bValid) return 1;
      return a.index - b.index;
    });

  let movableCursor = 0;
  const sorted = [];

  for (let i = 0; i < rows.length; i += 1) {
    const pinnedRow = pinnedByIndex.get(i);
    if (pinnedRow) {
      sorted.push(pinnedRow.row);
    } else {
      sorted.push(movableRows[movableCursor].row);
      movableCursor += 1;
    }
  }

  return sorted;
}

function normalizeLogbookRowForDisplay(row = {}, aircraft = {}) {
  const normalized = row && typeof row === 'object' ? row : {};
  const aircraftRow = aircraft && typeof aircraft === 'object' ? aircraft : {};

  const aircraftType = String(
    normalized.aircraftType
    || normalized.type
    || normalized.icao_type
    || aircraftRow.icao_type
    || ''
  );

  const aircraftRegistration = String(
    normalized.aircraftRegistration
    || normalized.registration
    || normalized.reg
    || aircraftRow.registration
    || ''
  );

  const pilot = String(normalized.pilot || normalized.pilotName || normalized.signature || '');
  const student = String(normalized.student || normalized.studentName || '');
  const to = String(normalized.to || normalized.arrivalAirfield || normalized.destination || '');
  const from = String(normalized.from || normalized.departureAirfield || normalized.origin || '');
  const depUtc = String(normalized.depUtc || normalized.takeoffTime || normalized.departureTime || '');
  const arrUtc = String(normalized.arrUtc || normalized.landingTime || normalized.arrivalTime || '');
  const notes = String(normalized.notes || normalized.remarks || '');

  return {
    ...normalized,
    aircraftType,
    aircraftRegistration,
    pilot,
    student,
    to,
    from,
    depUtc,
    arrUtc,
    notes,
  };
}

function computeDesktopSheetScale(width) {
  const viewport = Number(width) || 1440;
  return Math.min(1, (viewport - 70) / 2300);
}

function isMissingEndColumnsError(error) {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('end_date') || message.includes('end_time') || message.includes('column');
}

function isBookingsTimeOrderError(error) {
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '').toLowerCase();
  return code === 'PGRST100'
    || message.includes('failed to parse order')
    || (message.includes('order') && message.includes('time'));
}

async function runBookingsQueryWithTimeOrderFallback(buildQuery) {
  let result = await buildQuery(true);
  if (!result.error || !isBookingsTimeOrderError(result.error)) {
    return result;
  }

  result = await buildQuery(false);
  return result;
}

function getPilotPaperLogKey(userId) {
  const key = String(userId || '').trim();
  return key ? `paper-pilot-${key}` : '';
}

function getPilotManualEntryPrefix(userId) {
  const key = String(userId || '').trim();
  return key ? `manual-pilot-${key}-` : '';
}

function buildManualEntryKey(userId) {
  const prefix = getPilotManualEntryPrefix(userId);
  if (!prefix) return '';

  const randomPart = window?.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  return `${prefix}${randomPart}`;
}

function buildPaperPilotDraft(entry = {}) {
  const source = entry && typeof entry === 'object' ? entry : {};
  const legacyTypeReg = String(source.typeReg || '').trim();
  const [legacyType = '', ...legacyRegParts] = legacyTypeReg ? legacyTypeReg.split(/\s+/) : [];
  const legacyReg = legacyRegParts.join(' ');
  const broughtForwardSource = source.broughtForwardTimes && typeof source.broughtForwardTimes === 'object'
    ? source.broughtForwardTimes
    : {};

  return {
    initialHoursConfirmed: source.initialHoursConfirmed === true,
    timeFormat: normalizePilotTimeFormat(source.timeFormat),
    date: formatDateToDdMmYyyy(String(source.date || '')),
    type: String(source.type || legacyType || ''),
    reg: String(source.reg || legacyReg || ''),
    pilot: String(source.pilot || ''),
    student: String(source.student || ''),
    to: String(source.to || ''),
    from: String(source.from || ''),
    depUtc: String(source.takeoffTime || source.depUtc || ''),
    arrUtc: String(source.landingTime || source.arrUtc || ''),
    seDayDual: String(source.seDayDual || ''),
    seDayPic: String(source.seDayPic || ''),
    seNightDual: String(source.seNightDual || ''),
    seNightPic: String(source.seNightPic || ''),
    meDayDual: String(source.meDayDual || ''),
    meDayPic: String(source.meDayPic || ''),
    meDayCopilot: String(source.meDayCopilot || ''),
    meDayPiUs: String(source.meDayPiUs || ''),
    meNightDual: String(source.meNightDual || ''),
    meNightPic: String(source.meNightPic || ''),
    meNightCopilot: String(source.meNightCopilot || ''),
    meNightPiUs: String(source.meNightPiUs || ''),
    instrumentFlight: String(source.instrumentFlight || ''),
    otherFlying: String(source.otherFlying || ''),
    notes: String(source.notes || source.remarks || ''),
    broughtForwardTimes: {
      seDayDual: String(broughtForwardSource.seDayDual || source.bfSeDayDual || ''),
      seDayPic: String(broughtForwardSource.seDayPic || source.bfSeDayPic || ''),
      seNightDual: String(broughtForwardSource.seNightDual || source.bfSeNightDual || ''),
      seNightPic: String(broughtForwardSource.seNightPic || source.bfSeNightPic || ''),
      meDayDual: String(broughtForwardSource.meDayDual || source.bfMeDayDual || ''),
      meDayPic: String(broughtForwardSource.meDayPic || source.bfMeDayPic || ''),
      meDayCopilot: String(broughtForwardSource.meDayCopilot || source.bfMeDayCopilot || ''),
      meDayPiUs: String(broughtForwardSource.meDayPiUs || source.bfMeDayPiUs || ''),
      meNightDual: String(broughtForwardSource.meNightDual || source.bfMeNightDual || ''),
      meNightPic: String(broughtForwardSource.meNightPic || source.bfMeNightPic || ''),
      meNightCopilot: String(broughtForwardSource.meNightCopilot || source.bfMeNightCopilot || ''),
      meNightPiUs: String(broughtForwardSource.meNightPiUs || source.bfMeNightPiUs || ''),
      instrumentFlight: String(broughtForwardSource.instrumentFlight || source.bfInstrumentFlight || ''),
      otherFlying: String(broughtForwardSource.otherFlying || source.bfOtherFlying || ''),
    },
  };
}

function hasSavedInitialHours(source) {
  const paperSource =
    source?.pilotLogbookPaper
    || source?.pilotLogbook
    || source
    || {};

  if (paperSource.initialHoursConfirmed === true) {
    return true;
  }

  const broughtForward =
    paperSource.broughtForwardTimes
    || paperSource;

  const fields = [
    'seDayDual',
    'seDayPic',
    'seNightDual',
    'seNightPic',
    'meDayDual',
    'meDayPic',
    'meDayCopilot',
    'meDayPiUs',
    'meNightDual',
    'meNightPic',
    'meNightCopilot',
    'meNightPiUs',
    'instrumentFlight',
    'otherFlying',
  ];

  return fields.some(
    (field) =>
      String(broughtForward?.[field] || '')
        .trim() !== ''
  );
}

function isCancelledOrDidNotFlyStatus(status) {
  const normalized = String(status || '').trim().toLowerCase();
  return normalized === 'did_not_fly'
    || normalized === 'not_flown'
    || normalized === 'cancelled'
    || normalized === 'canceled'
    || normalized === 'deleted';
}

export function isEligibleBookingLinkedLogbookRow(row) {
  if (!row || row.isManual || row.isManualDraft || !row.bookingId) {
    return false;
  }

  return !isCancelledOrDidNotFlyStatus(row.techLogStatus);
}

function isBookingLinkedPilotLogbookEligible(bookingOrRow) {
  if (!bookingOrRow) return false;

  const booking = bookingOrRow.bookingId
    ? { tech_log_status: bookingOrRow.techLogStatus }
    : bookingOrRow;

  return getResolvedPostFlightStatus(booking) === 'completed';
}

function isCancelledOrDidNotFlyNotes(notes) {
  const text = String(notes || '').trim().toLowerCase();
  if (!text) return false;

  return text.includes('did not fly')
    || text.includes('did-not-fly')
    || text.includes('did_not_fly')
    || text.includes('not flown')
    || text.includes('cancelled')
    || text.includes('canceled');
}

function hasAnyPositiveHourValue(row = {}) {
  const hourFields = [
    'seDayDual',
    'seDayPic',
    'seNightDual',
    'seNightPic',
    'meDayDual',
    'meDayPic',
    'meDayCopilot',
    'meDayPiUs',
    'meNightDual',
    'meNightPic',
    'meNightCopilot',
    'meNightPiUs',
    'instrumentFlight',
    'otherFlying',
    'flightTime',
    'taxiTime',
    'totalTime',
  ];

  return hourFields.some((field) => {
    const value = Number(String(row[field] || '').replace(',', '.'));
    return Number.isFinite(value) && value > 0;
  });
}

function hasOperationalFlightData(row = {}) {
  const routeOrTimeFields = ['from', 'to', 'depUtc', 'arrUtc'];
  return routeOrTimeFields.some((field) => String(row[field] || '').trim() !== '');
}

export function applySelectedBookingPilotLabel(row, hasSavedPilotEntry) {
  if (!row || hasSavedPilotEntry) return row;

  return {
    ...row,
    pilot: 'SELF',
  };
}

function getQueryBookingId(search) {
  const params = new URLSearchParams(String(search || ''));
  return String(params.get('bookingId') || '').trim();
}

function buildSelectedFlightDraft({ booking, techEntry, defaultPilotName, aircraft }) {
  const pilotEntry = techEntry?.pilotLogbook || {};
  const isTrainingFlight = Boolean(
    booking?.course_enrolment_id ||
      booking?.instructor_user_id ||
      booking?.student_user_id ||
      pilotEntry.isTrainingFlight === true
  );
  const plannedBriefing = booking?.briefing_data && typeof booking.briefing_data === 'object'
    ? booking.briefing_data
    : {};
  const plannedDepartureAirfield = booking?.departure_airfield || plannedBriefing.departureAirfield || '';
  const plannedArrivalAirfield = booking?.destination_airfield || plannedBriefing.destinationAirfield || '';
  const flightTimeRaw = Number(pilotEntry.flightTime ?? techEntry?.flightTime ?? 0);
  const taxiTimeRaw = Number(pilotEntry.taxiTime ?? techEntry?.taxiTime ?? 0);
  const totalTimeRaw = Number(pilotEntry.totalTime);
  const resolvedFlightTime = Number.isFinite(flightTimeRaw) && flightTimeRaw > 0
    ? flightTimeRaw
    : 0;
  const resolvedTaxiTime = Number.isFinite(taxiTimeRaw) && taxiTimeRaw > 0
    ? taxiTimeRaw
    : 0;
  const resolvedTotalTime = Number.isFinite(totalTimeRaw) && totalTimeRaw > 0
    ? totalTimeRaw
    : Number((resolvedFlightTime + resolvedTaxiTime).toFixed(1));

  return {
    bookingId: String(booking?.id || ''),
    aircraftId: String(booking?.aircraft_id || ''),
    date: formatDateToDdMmYyyy(String(booking?.date || '')),
    time: String(booking?.time || ''),
    pilot: String(pilotEntry.pilot || techEntry?.pilotName || techEntry?.signature || defaultPilotName),
    student: String(pilotEntry.student || techEntry?.student || ''),
    to: String(pilotEntry.to || techEntry?.arrivalAirfield || plannedArrivalAirfield || ''),
    from: String(pilotEntry.from || techEntry?.departureAirfield || plannedDepartureAirfield || ''),
    depUtc: String(pilotEntry.depUtc || pilotEntry.takeoffTime || techEntry?.departureTime || booking?.time || ''),
    arrUtc: String(pilotEntry.arrUtc || pilotEntry.landingTime || techEntry?.arrivalTime || booking?.end_time || ''),
    seDayDual: String(pilotEntry.seDayDual || ''),
    seDayPic: String(pilotEntry.seDayPic || ''),
    seNightDual: String(pilotEntry.seNightDual || ''),
    seNightPic: String(pilotEntry.seNightPic || ''),
    meDayDual: String(pilotEntry.meDayDual || ''),
    meDayPic: String(pilotEntry.meDayPic || ''),
    meDayCopilot: String(pilotEntry.meDayCopilot || ''),
    meDayPiUs: String(pilotEntry.meDayPiUs || ''),
    meNightDual: String(pilotEntry.meNightDual || ''),
    meNightPic: String(pilotEntry.meNightPic || ''),
    meNightCopilot: String(pilotEntry.meNightCopilot || ''),
    meNightPiUs: String(pilotEntry.meNightPiUs || ''),
    instrumentFlight: String(pilotEntry.instrumentFlight || ''),
    otherFlying: String(pilotEntry.otherFlying || ''),
    flightTime: resolvedFlightTime,
    taxiTime: resolvedTaxiTime,
    totalTime: resolvedTotalTime,
    notes: String(
      pilotEntry.notes ||
        (
          techEntry.simulationOnly === true
            ? techEntry.simulationLabel ||
              techEntry.notes ||
              ''
            : ''
        )
    ),
    savedAt: String(pilotEntry.savedAt || techEntry?.pilotLogSavedAt || ''),
    techLogStatus: String(booking?.tech_log_status || ''),
    isTrainingFlight,
    aircraftType: String(aircraft?.icao_type || ''),
    aircraftRegistration: String(aircraft?.registration || ''),
  };
}

function normalizeVisitedAirport(value) {
  const cleaned = String(value || '')
    .trim()
    .replace(/\s+/g, ' ');

  if (!cleaned) return '';

  const upperValue = cleaned.toUpperCase();

  if (
    upperValue === '-'
    || upperValue === 'N/A'
    || upperValue === 'NONE'
  ) {
    return '';
  }

  return upperValue;
}

function getLogbookDateSortValue(value) {
  const text = String(value || '').trim();

  const ukDateMatch = text.match(
    /^(\d{2})\/(\d{2})\/(\d{4})$/
  );

  if (ukDateMatch) {
    return Date.UTC(
      Number(ukDateMatch[3]),
      Number(ukDateMatch[2]) - 1,
      Number(ukDateMatch[1])
    );
  }

  const parsedValue = Date.parse(text);

  return Number.isFinite(parsedValue)
    ? parsedValue
    : 0;
}

export function buildAirportsVisitedSummary(
  logbookRows = []
) {
  const airportMap = new Map();

  (logbookRows || []).forEach((row) => {
    if (!row || row.isManualDraft) {
      return;
    }

    if (
      isCancelledOrDidNotFlyStatus(row.techLogStatus)
      || isCancelledOrDidNotFlyNotes(row.notes)
    ) {
      return;
    }

    const rowDate = String(row.date || '').trim();
    const rowDateValue = getLogbookDateSortValue(rowDate);

    const locations = [
      {
        airport: normalizeVisitedAirport(row.from),
        isDeparture: true,
        isArrival: false,
      },
      {
        airport: normalizeVisitedAirport(row.to),
        isDeparture: false,
        isArrival: true,
      },
    ];

    const airportsSeenOnThisFlight = new Set();

    locations.forEach((location) => {
      const airport = location.airport;

      if (!airport) return;

      if (!airportMap.has(airport)) {
        airportMap.set(airport, {
          airport,
          visits: 0,
          arrivals: 0,
          departures: 0,
          firstVisit: rowDate,
          firstVisitValue: rowDateValue,
          lastVisit: rowDate,
          lastVisitValue: rowDateValue,
        });
      }

      const entry = airportMap.get(airport);

      if (!airportsSeenOnThisFlight.has(airport)) {
        entry.visits += 1;
        airportsSeenOnThisFlight.add(airport);
      }

      if (location.isArrival) {
        entry.arrivals += 1;
      }

      if (location.isDeparture) {
        entry.departures += 1;
      }

      if (
        rowDateValue > 0
        && (
          entry.firstVisitValue <= 0
          || rowDateValue < entry.firstVisitValue
        )
      ) {
        entry.firstVisit = rowDate;
        entry.firstVisitValue = rowDateValue;
      }

      if (
        rowDateValue > 0
        && rowDateValue > entry.lastVisitValue
      ) {
        entry.lastVisit = rowDate;
        entry.lastVisitValue = rowDateValue;
      }
    });
  });

  return Array.from(airportMap.values())
    .sort((first, second) => (
      second.visits - first.visits
      || first.airport.localeCompare(second.airport)
    ));
}
export default function PilotLogbookPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const user = session?.user;
  const isPhone = useMediaQuery(MEDIA_QUERIES.phone);
  const selectedBookingId = getQueryBookingId(location.search);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [aircraftMap, setAircraftMap] = useState({});
  const [paperLogDraft, setPaperLogDraft] = useState(buildPaperPilotDraft());
const [techLogMap, setTechLogMap] = useState({});
  const [techLogVersionMap, setTechLogVersionMap] = useState({});
  const [pilotTimeFormat, setPilotTimeFormat] = useState(LOGBOOK_TIME_FORMAT_DECIMAL_HOURS);
  const [showLogbookSettings, setShowLogbookSettings] = useState(false);
  const [showLogbookTools, setShowLogbookTools] = useState(false);
  const [showAirportsVisited, setShowAirportsVisited] = useState(false);
  const [showLogbookStatistics, setShowLogbookStatistics] = useState(false);
  const [airportSearch, setAirportSearch] = useState('');
  const [logbookSearch, setLogbookSearch] = useState('');
  const [logbookPeriodFilter, setLogbookPeriodFilter] = useState('all');
  const [logbookCapacityFilter, setLogbookCapacityFilter] = useState('all');
  const [logbookSortOrder, setLogbookSortOrder] = useState('newest');
  const [showLogbookFilters, setShowLogbookFilters] = useState(false);
  const [savingPilotTimeFormat, setSavingPilotTimeFormat] = useState(false);
  const [pilotTimeFormatFeedback, setPilotTimeFormatFeedback] = useState({ type: '', text: '' });
  const [selectedFlightDraft, setSelectedFlightDraft] = useState(null);
  const [selectedFlightHasExistingEntry, setSelectedFlightHasExistingEntry] = useState(false);
  const [selectedBookingDidNotFly, setSelectedBookingDidNotFly] = useState(false);
  const [selectedBookingResolution, setSelectedBookingResolution] = useState('none');
  const [selectedFlightFeedback, setSelectedFlightFeedback] = useState({ type: '', text: '' });
  // True when a genuine Pilot Logbook entry is saved but the booking was never
  // marked complete (e.g. the pre-atomic save/complete race). Offers a durable
  // retry rather than a passive warning.
  const [selectedBookingCompletionMismatch, setSelectedBookingCompletionMismatch] = useState(false);
  const [retryingBookingCompletion, setRetryingBookingCompletion] = useState(false);

const [savingRowId, setSavingRowId] = useState('');
const [savingInitialHours, setSavingInitialHours] = useState(false);
const [initialHoursFeedback, setInitialHoursFeedback] = useState({ type: '', text: '' });
const [initialHoursSaved, setInitialHoursSaved] = useState(false);
const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth || 1440);
const [desktopSheetScale, setDesktopSheetScale] = useState(() => computeDesktopSheetScale(window.innerWidth || 1440));
const [utcFieldErrors, setUtcFieldErrors] = useState({});
const [rowFieldErrors, setRowFieldErrors] = useState({});
const [dirtyRowIds, setDirtyRowIds] = useState({});
const [savingAllChanges, setSavingAllChanges] = useState(false);
const [bulkSaveFeedback, setBulkSaveFeedback] = useState({ type: '', text: '' });
const [overAllocationMessagesByRow, setOverAllocationMessagesByRow] = useState({});
const [selectedManualRowIds, setSelectedManualRowIds] = useState({});
const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);
const [deletingSelectedManualRows, setDeletingSelectedManualRows] = useState(false);
useEffect(() => {
  const globalLogoLink =
    document.querySelector('.company-logo-link');

  if (!globalLogoLink) {
    return undefined;
  }

  const previousDisplay =
    globalLogoLink.style.display;

  globalLogoLink.style.display = 'none';

  return () => {
    globalLogoLink.style.display = previousDisplay;
  };
}, []);

const savedFlightEntriesCount = rows.filter((row) => isGenuineSavedFlightEntry(row)).length;
const initialHoursLocked = savedFlightEntriesCount >= 3;
const useMobileTableScroll = viewportWidth < 1180;
const hasManualDraft = rows.some((row) => row.isManualDraft);
const selectedTimeFormatLabel = pilotTimeFormat === LOGBOOK_TIME_FORMAT_HOURS_MINUTES
  ? 'Hours and minutes'
  : 'Decimal hours';

const hourFieldKeys = [
  'seDayDual',
  'seDayPic',
  'seNightDual',
  'seNightPic',
  'meDayDual',
  'meDayPic',
  'meDayCopilot',
  'meDayPiUs',
  'meNightDual',
  'meNightPic',
  'meNightCopilot',
  'meNightPiUs',
  'instrumentFlight',
  'otherFlying',
];

const hourFieldLabels = {
  seDayDual: 'Dual (1)',
  seDayPic: 'P in C (2)',
  seNightDual: 'Dual (3)',
  seNightPic: 'P in C (4)',
  meDayDual: 'Dual (5)',
  meDayPic: 'P in C (6)',
  meDayCopilot: 'Co-pilot (7)',
  meDayPiUs: 'PI (US) (8)',
  meNightDual: 'Dual (9)',
  meNightPic: 'P in C (10)',
  meNightCopilot: 'Co-pilot (11)',
  meNightPiUs: 'PI (US) (12)',
  instrumentFlight: 'Instrument Flight (13)',
  otherFlying: 'Other Flying (14)',
};

const isEligibleAllocationField = (field) => ALLOCATION_ELIGIBLE_FIELDS.includes(field);

const toHoursNumber = (value) => {
  return parseHourValueToNumber(value);
};

const formatHoursTotal = (value) => {
  return formatHourNumberForDisplay(value, pilotTimeFormat);
};

const markRowDirty = (bookingId) => {
  const key = String(bookingId || '');
  if (!key) return;
  setDirtyRowIds((prev) => ({ ...prev, [key]: true }));
};

const clearRowError = (bookingId, field) => {
  const errorKey = getUtcFieldErrorKey(bookingId, field);
  setRowFieldErrors((prev) => {
    if (!prev[errorKey]) return prev;
    const next = { ...prev };
    delete next[errorKey];
    return next;
  });
};

const unsavedChangeCount = Object.keys(dirtyRowIds).length;
const hasUnsavedLogbookChanges = unsavedChangeCount > 0;
const displayRows = useMemo(() => {
  const searchText =
    logbookSearch.trim().toUpperCase();

  const now = new Date();
  const currentYear = now.getUTCFullYear();

  const startOfCurrentYear =
    Date.UTC(currentYear, 0, 1);

  const startOfPreviousYear =
    Date.UTC(currentYear - 1, 0, 1);

  const todayUtc = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate()
  );

  const startOfLast90Days =
    todayUtc - (89 * 24 * 60 * 60 * 1000);

  const hasPositiveTime = (
    row,
    fieldNames
  ) => fieldNames.some((fieldName) => {
    const value = fieldName === 'seDayPic'
      ? resolveSeDayPicValueForSave(row)
      : fieldName === 'seDayDual'
        ? resolveSeDayDualValueForSave(row)
        : row?.[fieldName];

    const hours = toHoursNumber(value);

    return Number.isFinite(hours) && hours > 0;
  });

  const filteredRows = rows.filter((row) => {
    if (!row) return false;

    // Never hide a new manual entry while it is being completed.
    if (row.isManualDraft) return true;

    const aircraft =
      aircraftMap[String(row.aircraftId)] || {};

    const rowDateValue =
      getLogbookDateSortValue(row.date);

    let matchesPeriod = true;

    if (logbookPeriodFilter === 'this-year') {
      matchesPeriod =
        rowDateValue >= startOfCurrentYear;
    } else if (
      logbookPeriodFilter === 'last-90-days'
    ) {
      matchesPeriod =
        rowDateValue >= startOfLast90Days;
    } else if (
      logbookPeriodFilter === 'previous-year'
    ) {
      matchesPeriod =
        rowDateValue >= startOfPreviousYear
        && rowDateValue < startOfCurrentYear;
    }

    let matchesCapacity = true;

    if (logbookCapacityFilter === 'pic') {
      matchesCapacity = hasPositiveTime(row, [
        'seDayPic',
        'seNightPic',
        'meDayPic',
        'meNightPic',
      ]);
    } else if (
      logbookCapacityFilter === 'dual'
    ) {
      matchesCapacity = hasPositiveTime(row, [
        'seDayDual',
        'seNightDual',
        'meDayDual',
        'meNightDual',
      ]);
    } else if (
      logbookCapacityFilter === 'night'
    ) {
      matchesCapacity = hasPositiveTime(row, [
        'seNightDual',
        'seNightPic',
        'meNightDual',
        'meNightPic',
        'meNightCopilot',
        'meNightPiUs',
      ]);
    } else if (
      logbookCapacityFilter === 'instrument'
    ) {
      matchesCapacity = hasPositiveTime(row, [
        'instrumentFlight',
      ]);
    }

    const searchableText = [
      row.date,
      row.aircraftType,
      row.aircraftRegistration,
      row.type,
      row.reg,
      aircraft.icao_type,
      aircraft.registration,
      row.from,
      row.to,
      row.pilot,
      row.student,
      row.notes,
    ]
      .map((value) => String(value || ''))
      .join(' ')
      .toUpperCase();

    const matchesSearch =
      !searchText
      || searchableText.includes(searchText);

    return (
      matchesPeriod
      && matchesCapacity
      && matchesSearch
    );
  });

  return [...filteredRows].sort(
    (first, second) => {
      if (first.isManualDraft) return 1;
      if (second.isManualDraft) return -1;

      const firstDate =
        getLogbookDateSortValue(first.date);

      const secondDate =
        getLogbookDateSortValue(second.date);

      const dateDifference =
        logbookSortOrder === 'oldest'
          ? firstDate - secondDate
          : secondDate - firstDate;

      if (dateDifference !== 0) {
        return dateDifference;
      }

      const firstTime =
        String(first.depUtc || first.time || '');

      const secondTime =
        String(second.depUtc || second.time || '');

      return logbookSortOrder === 'oldest'
        ? firstTime.localeCompare(secondTime)
        : secondTime.localeCompare(firstTime);
    }
  );
}, [
  rows,
  aircraftMap,
  logbookSearch,
  logbookPeriodFilter,
  logbookCapacityFilter,
  logbookSortOrder,
]);
const firstOverAllocatedRow = rows.find((row) => overAllocationMessagesByRow[String(row.bookingId)]);
const overAllocationMessage = firstOverAllocatedRow
  ? overAllocationMessagesByRow[String(firstOverAllocatedRow.bookingId)]
  : '';
const hasOverAllocatedRows = Boolean(overAllocationMessage);
const topFeedbackText = overAllocationMessage || bulkSaveFeedback.text;
const topFeedbackType = overAllocationMessage ? 'error' : bulkSaveFeedback.type;

const getDisplayRowNumber = (bookingId) => {
  const index = displayRows.findIndex((row) => String(row.bookingId) === String(bookingId));
  return index >= 0 ? index + 1 : null;
};

const isSelectableManualRow = (row) => Boolean(row?.isManual && !row?.isManualDraft && !row?.isDraft);
const selectedManualIds = Object.keys(selectedManualRowIds).filter((bookingId) => selectedManualRowIds[bookingId]);
const hasSelectedManualRows = selectedManualIds.length > 0;

useEffect(() => {
  setSelectedManualRowIds((prev) => {
    const selectableIds = new Set(displayRows.filter((row) => isSelectableManualRow(row)).map((row) => String(row.bookingId)));
    const next = {};
    Object.keys(prev).forEach((bookingId) => {
      if (prev[bookingId] && selectableIds.has(bookingId)) {
        next[bookingId] = true;
      }
    });
    const unchanged = Object.keys(prev).length === Object.keys(next).length
      && Object.keys(prev).every((key) => prev[key] === next[key]);
    return unchanged ? prev : next;
  });
}, [displayRows]);

useEffect(() => {
  const nextMessages = {};

  rows.forEach((row) => {
    const allocationSummary = calculateAllocationSummaryForRow(row, pilotTimeFormat);
    if (!allocationSummary.hasDuration || !allocationSummary.isOverAllocated) return;

    const overByText = formatDurationMinutesForDisplay(Math.abs(allocationSummary.remainingMinutes), pilotTimeFormat);

    // Label using the same visible order the table shows, not raw fetch order,
    // so "Row N" always matches what the pilot sees on screen.
    const visibleRowIndex = displayRows.findIndex((displayRow) => String(displayRow.bookingId) === String(row.bookingId));
    let rowLabel;

    if (visibleRowIndex >= 0) {
      rowLabel = `Row ${visibleRowIndex + 1}`;
    } else {
      const registration =
        aircraftMap[String(row.aircraftId)]?.registration
        || row.aircraftRegistration
        || row.reg
        || '';
      const dateLabel = row.date || 'unknown date';
      rowLabel = `The flight on ${dateLabel}${registration ? ` (${registration})` : ''}`;
    }

    nextMessages[String(row.bookingId)] = `${rowLabel}: allocated time exceeds flight duration by ${overByText}.`;
  });

  setOverAllocationMessagesByRow((prev) => {
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(nextMessages);
    if (prevKeys.length === nextKeys.length && prevKeys.every((key) => prev[key] === nextMessages[key])) {
      return prev;
    }
    return nextMessages;
  });
}, [rows, pilotTimeFormat, displayRows, aircraftMap]);

const liveCarriedForwardTotals = hourFieldKeys.reduce((acc, field) => {
  acc[field] = toHoursNumber(paperLogDraft.broughtForwardTimes?.[field]);
  return acc;
}, {});

rows.forEach((row) => {
  hourFieldKeys.forEach((field) => {
    const value = field === 'seDayPic'
      ? resolveSeDayPicValueForSave(row)
      : field === 'seDayDual'
        ? resolveSeDayDualValueForSave(row)
        : row[field];

    liveCarriedForwardTotals[field] +=
      toHoursNumber(value);
  });
});

  useEffect(() => {
    function handleResize() {
      const nextWidth = window.innerWidth || 1440;
      setViewportWidth(nextWidth);
      setDesktopSheetScale(computeDesktopSheetScale(nextWidth));
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      setSelectedBookingResolution('none');

      if (!user?.id) {
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      const aircraftPromise = supabase.from('aircraft').select('id, registration, icao_type, engine_count');

      let bookingsResult = await runBookingsQueryWithTimeOrderFallback((includeTimeOrder) => {
        let query = supabase
          .from('bookings')
          .select('id, user_id, aircraft_id, date, time, duration, tech_log_status, briefing_data, pilot_logbook_status, pilot_logbook_completed_at, pilot_logbook_saved_at, pilot_logbook_saved, logbook_status, logbook_completed_at, student_user_id, instructor_user_id, course_enrolment_id')
          .eq('user_id', user.id)
          .order('date', { ascending: true });

        if (includeTimeOrder) query = query.order('time', { ascending: true });
        return query;
      });

      if (bookingsResult.error && isMissingEndColumnsError(bookingsResult.error)) {
        const fallback = await runBookingsQueryWithTimeOrderFallback((includeTimeOrder) => {
          let query = supabase
            .from('bookings')
            .select('id, user_id, aircraft_id, date, time, duration, tech_log_status, briefing_data')
            .eq('user_id', user.id)
            .order('date', { ascending: true });

          if (includeTimeOrder) query = query.order('time', { ascending: true });
          return query;
        });

        bookingsResult = {
          data: (fallback.data || []).map((row) => ({ ...row, end_date: null, end_time: null })),
          error: fallback.error,
        };
      }

      const [{ data: aircraftRows }, { data: bookingRows, error: bookingError }] = await Promise.all([
        aircraftPromise,
        Promise.resolve(bookingsResult),
      ]);

      if (cancelled) return;
      if (bookingError) {
        setError('Could not load pilot logbook entries.');
        setLoading(false);
        return;
      }

      const aircraftIndex = {};
      (aircraftRows || []).forEach((row) => {
        aircraftIndex[String(row.id)] = row;
      });
      setAircraftMap(aircraftIndex);

      const safeBookings = bookingRows || [];

      const bookingIds = safeBookings.map((row) => String(row.id));
      const paperLogKey = getPilotPaperLogKey(user.id);
      const manualKeyPrefix = getPilotManualEntryPrefix(user.id);
      const keysToLoad = paperLogKey ? [...bookingIds, paperLogKey] : bookingIds;
      const { data: officialRows } = await fetchOfficialTechLogsForBookings(keysToLoad);
      const indexed = indexOfficialTechLogs(officialRows || []);
      const loadedVersionMap = {};
      (officialRows || []).forEach((row) => {
        const bookingKey = String(row?.booking_id || '').trim();
        const version = Number(row?.version);
        if (bookingKey && Number.isFinite(version)) {
          loadedVersionMap[bookingKey] = version;
        }
      });
      setTechLogMap(indexed.logMap || {});
      setTechLogVersionMap(loadedVersionMap);

      const { data: manualLogRows, error: manualLogsError } = manualKeyPrefix
        ? await supabase
          .from('official_tech_logs')
          .select('booking_id, data, version, updated_at, updated_by')
          .like('booking_id', `${manualKeyPrefix}%`)
        : { data: [], error: null };

      if (manualLogsError) {
        setError('Could not load pilot logbook entries.');
        setLoading(false);
        return;
      }


      if (paperLogKey) {
        const paperEntryRaw = indexed.logMap[paperLogKey] || {};
        const paperSource = paperEntryRaw.pilotLogbookPaper || paperEntryRaw.pilotLogbook || paperEntryRaw;
        const nextPaperDraft = buildPaperPilotDraft(paperSource);
        setPaperLogDraft(nextPaperDraft);
        setPilotTimeFormat(normalizePilotTimeFormat(nextPaperDraft.timeFormat));
        const alreadySaved = hasSavedInitialHours(paperEntryRaw);
        setInitialHoursSaved(alreadySaved);
      } else {
        setPaperLogDraft(buildPaperPilotDraft());
        setPilotTimeFormat(LOGBOOK_TIME_FORMAT_DECIMAL_HOURS);
        setInitialHoursSaved(false);
      }

      let nextRows = safeBookings
        .map((booking) => {
          const techEntry = indexed.logMap[String(booking.id)] || {};
          const pilotEntry = techEntry.pilotLogbook || {};
          const plannedBriefing = booking?.briefing_data && typeof booking.briefing_data === 'object'
            ? booking.briefing_data
            : {};
          const plannedDepartureAirfield = booking?.departure_airfield || plannedBriefing.departureAirfield || '';
          const plannedArrivalAirfield = booking?.destination_airfield || plannedBriefing.destinationAirfield || '';
          const flightTime = Number(pilotEntry.flightTime ?? techEntry.flightTime);
          const taxiTime = Number(pilotEntry.taxiTime ?? techEntry.taxiTime);
          const totalTime = Number(pilotEntry.totalTime);

          const resolvedFlightTime = Number.isFinite(flightTime) ? flightTime : 0;
          const resolvedTaxiTime = Number.isFinite(taxiTime) ? taxiTime : 0;
          const resolvedTotal = Number.isFinite(totalTime)
            ? totalTime
            : Number((resolvedFlightTime + resolvedTaxiTime).toFixed(1));

          const hasMeaningfulEntry = Object.keys(pilotEntry || {}).length > 0
            || Number.isFinite(flightTime)
            || Number.isFinite(taxiTime)
            || !!techEntry.pilotLogSavedAt;

          if (!hasMeaningfulEntry) return null;

          return {
            bookingId: String(booking.id),
            aircraftId: String(booking.aircraft_id),
            date: formatDateToDdMmYyyy(String(booking.date || '')),
            time: booking.time || '',
            pilot: String(pilotEntry.pilot || techEntry.pilotName || techEntry.signature || user?.user_metadata?.full_name || ''),
            student: String(techEntry.student || pilotEntry.student || ''),
            to: String(pilotEntry.to || techEntry.arrivalAirfield || plannedArrivalAirfield || ''),
            from: String(pilotEntry.from || techEntry.departureAirfield || plannedDepartureAirfield || ''),
            depUtc: String(pilotEntry.depUtc || pilotEntry.takeoffTime || techEntry.departureTime || booking.time || ''),
            arrUtc: String(pilotEntry.arrUtc || pilotEntry.landingTime || techEntry.arrivalTime || booking.end_time || ''),
            seDayDual: normalizeHourValueForSave(pilotEntry.seDayDual),
            seDayPic: normalizeHourValueForSave(pilotEntry.seDayPic),
            seNightDual: normalizeHourValueForSave(pilotEntry.seNightDual),
            seNightPic: normalizeHourValueForSave(pilotEntry.seNightPic),
            meDayDual: normalizeHourValueForSave(pilotEntry.meDayDual),
            meDayPic: normalizeHourValueForSave(pilotEntry.meDayPic),
            meDayCopilot: normalizeHourValueForSave(pilotEntry.meDayCopilot),
            meDayPiUs: normalizeHourValueForSave(pilotEntry.meDayPiUs),
            meNightDual: normalizeHourValueForSave(pilotEntry.meNightDual),
            meNightPic: normalizeHourValueForSave(pilotEntry.meNightPic),
            meNightCopilot: normalizeHourValueForSave(pilotEntry.meNightCopilot),
            meNightPiUs: normalizeHourValueForSave(pilotEntry.meNightPiUs),
            instrumentFlight: normalizeHourValueForSave(pilotEntry.instrumentFlight),
            otherFlying: normalizeHourValueForSave(pilotEntry.otherFlying),
            flightTime: resolvedFlightTime,
            taxiTime: resolvedTaxiTime,
            totalTime: resolvedTotal,
            notes: String(pilotEntry.notes || ''),
            savedAt: pilotEntry.savedAt || techEntry.pilotLogSavedAt || '',
            techLogStatus: String(booking.tech_log_status || ''),
            isTrainingFlight: Boolean(
              booking.course_enrolment_id ||
                booking.instructor_user_id ||
                booking.student_user_id ||
                pilotEntry.isTrainingFlight === true
            ),
            autoDurationMinutes: null,
            seDayPicUserEdited: String(pilotEntry.seDayPic || '').trim() !== '',
          };
        })
        .filter(Boolean);

      const manualRows = (manualLogRows || []).map((row) => {
        const entry = row?.data || {};
        const pilotEntry = entry?.pilotLogbook || {};
        const flightTime = Number(pilotEntry.flightTime ?? entry?.flightTime);
        const taxiTime = Number(pilotEntry.taxiTime ?? entry?.taxiTime);
        const totalTime = Number(pilotEntry.totalTime);

        const resolvedFlightTime = Number.isFinite(flightTime) ? flightTime : 0;
        const resolvedTaxiTime = Number.isFinite(taxiTime) ? taxiTime : 0;
        const resolvedTotal = Number.isFinite(totalTime)
          ? totalTime
          : Number((resolvedFlightTime + resolvedTaxiTime).toFixed(1));

        return {
          bookingId: String(row.booking_id || ''),
          aircraftId: '',
          date: formatDateToDdMmYyyy(String(pilotEntry.date || '')),
          time: '',
          pilot: String(pilotEntry.pilot || ''),
          student: String(pilotEntry.student || ''),
          to: String(pilotEntry.to || ''),
          from: String(pilotEntry.from || ''),
          depUtc: String(pilotEntry.depUtc || pilotEntry.takeoffTime || ''),
          arrUtc: String(pilotEntry.arrUtc || pilotEntry.landingTime || ''),
          seDayDual: normalizeHourValueForSave(pilotEntry.seDayDual),
          seDayPic: normalizeHourValueForSave(pilotEntry.seDayPic),
          seNightDual: normalizeHourValueForSave(pilotEntry.seNightDual),
          seNightPic: normalizeHourValueForSave(pilotEntry.seNightPic),
          meDayDual: normalizeHourValueForSave(pilotEntry.meDayDual),
          meDayPic: normalizeHourValueForSave(pilotEntry.meDayPic),
          meDayCopilot: normalizeHourValueForSave(pilotEntry.meDayCopilot),
          meDayPiUs: normalizeHourValueForSave(pilotEntry.meDayPiUs),
          meNightDual: normalizeHourValueForSave(pilotEntry.meNightDual),
          meNightPic: normalizeHourValueForSave(pilotEntry.meNightPic),
          meNightCopilot: normalizeHourValueForSave(pilotEntry.meNightCopilot),
          meNightPiUs: normalizeHourValueForSave(pilotEntry.meNightPiUs),
          instrumentFlight: normalizeHourValueForSave(pilotEntry.instrumentFlight),
          otherFlying: normalizeHourValueForSave(pilotEntry.otherFlying),
          flightTime: resolvedFlightTime,
          taxiTime: resolvedTaxiTime,
          totalTime: resolvedTotal,
          notes: String(pilotEntry.notes || ''),
          savedAt: String(pilotEntry.savedAt || ''),
          techLogStatus: 'manual',
          aircraftType: String(pilotEntry.type || ''),
          aircraftRegistration: String(pilotEntry.reg || ''),
          isManual: true,
          isDraft: false,
          isManualDraft: false,
          autoDurationMinutes: null,
          seDayPicUserEdited: String(pilotEntry.seDayPic || '').trim() !== '',
        };
      }).filter((row) => row.bookingId);

      manualRows.forEach((manualRow) => {
        const existingIndex = nextRows.findIndex((existing) => String(existing.bookingId) === String(manualRow.bookingId));
        if (existingIndex >= 0) {
          nextRows[existingIndex] = { ...nextRows[existingIndex], ...manualRow };
        } else {
          nextRows.push(manualRow);
        }
      });

      if (selectedBookingId) {
        const selectedBooking = safeBookings.find((booking) => String(booking.id) === selectedBookingId) || null;
        const selectedTechEntry = indexed.logMap[selectedBookingId] || {};
        const hasSavedPilotEntry = hasSavedPilotLogbookEntry(selectedTechEntry, selectedBooking?.tech_log_status);
        const selectedBookingEligible =
          isBookingLinkedPilotLogbookEligible(selectedBooking);

        setSelectedBookingCompletionMismatch(
          hasPersistentPilotLogbookCompletionMismatch(selectedBooking, selectedTechEntry)
        );

        setSelectedBookingDidNotFly(
          Boolean(selectedBooking) && isCancelledOrDidNotFlyStatus(selectedBooking.tech_log_status)
        );

        if (!selectedBooking) {
          setSelectedBookingResolution('missing');
        } else if (
          !isCancelledOrDidNotFlyStatus(selectedBooking.tech_log_status) &&
          (selectedBookingEligible || hasSavedPilotEntry)
        ) {
          setSelectedBookingResolution('eligible');
        } else if (!isCancelledOrDidNotFlyStatus(selectedBooking.tech_log_status)) {
          setSelectedBookingResolution('ineligible');
        }

        // A cancelled/did-not-fly booking must not create or surface a logbook draft row.
        if (
          selectedBooking &&
          (
            selectedBookingEligible ||
            hasSavedPilotEntry
          ) &&
          !isCancelledOrDidNotFlyStatus(selectedBooking.tech_log_status)
        ) {
          const selectedAircraft = aircraftIndex[String(selectedBooking.aircraft_id)] || {};
          const selectedDraft = buildSelectedFlightDraft({
            booking: selectedBooking,
            techEntry: selectedTechEntry,
            defaultPilotName: user?.user_metadata?.full_name || user?.email || '',
            aircraft: selectedAircraft,
          });
          const existingRowIndex = nextRows.findIndex((row) => String(row.bookingId) === selectedBookingId);
          const existingRow = existingRowIndex >= 0 ? nextRows[existingRowIndex] : null;

          const baseSelectedRow = existingRow
            ? {
              ...selectedDraft,
              ...existingRow,
              aircraftType: selectedDraft.aircraftType,
              aircraftRegistration: selectedDraft.aircraftRegistration,
            }
            : selectedDraft;

          if (hasSavedPilotEntry) {
            const completedRow = {
              ...baseSelectedRow,
              isDraft: false,
              seDayPicUserEdited: String(baseSelectedRow.seDayPic || '').trim() !== '',
            };
            setSelectedFlightDraft(completedRow);
            setSelectedFlightHasExistingEntry(true);

            if (existingRowIndex >= 0) {
              nextRows[existingRowIndex] = completedRow;
            } else {
              nextRows = [...nextRows, completedRow];
            }
          } else {
            const draftRow = applyAutoDurationToDraftRow({
              ...applySelectedBookingPilotLabel(
                baseSelectedRow,
                hasSavedPilotEntry
              ),
              isDraft: true,
              seDayPicUserEdited: false,
            });
            setSelectedFlightDraft(draftRow);
            setSelectedFlightHasExistingEntry(false);

            if (existingRowIndex >= 0) {
              nextRows[existingRowIndex] = draftRow;
            } else {
              nextRows = [...nextRows, draftRow];
            }
          }
        } else {
          setSelectedFlightDraft(null);
          setSelectedFlightHasExistingEntry(false);
        }
      } else {
        setSelectedFlightDraft(null);
        setSelectedFlightHasExistingEntry(false);
        setSelectedBookingDidNotFly(false);
        setSelectedBookingResolution('none');
        setSelectedBookingCompletionMismatch(false);
      }

      setRows(sortPilotLogbookRowsForDisplay(nextRows.map((row) => {
        return normalizeRowDateForDisplay(applyAutoDurationToDraftRow({
          ...row,
          seDayPicUserEdited: Boolean(row.seDayPicUserEdited),
        }));
      }), {}));
      const selectedBookingForNeedsSave = safeBookings.find(
        (booking) => String(booking.id) === selectedBookingId
      );
      const selectedBookingNeedsSave =
        Boolean(selectedBookingId) &&
        isBookingLinkedPilotLogbookEligible(selectedBookingForNeedsSave) &&
        !hasSavedPilotLogbookEntry(
          indexed.logMap[selectedBookingId] || {},
          selectedBookingForNeedsSave?.tech_log_status
        );

      setDirtyRowIds(
        selectedBookingNeedsSave
          ? { [selectedBookingId]: true }
          : {}
      );
      setBulkSaveFeedback({ type: '', text: '' });
      setRowFieldErrors({});
      setLoading(false);
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [selectedBookingId, user?.email, user?.id, user?.user_metadata?.full_name]);
function updateLogbookCell(bookingId, field, value) {
  const normalizedValue = typeof value === 'string' ? value.toUpperCase() : value;

  setRows((prev) => prev.map((row) => {
    if (String(row.bookingId) !== String(bookingId)) return row;
    const nextRow = {
      ...row,
      [field]: normalizedValue,
      ...(field === 'seDayPic' ? { seDayPicUserEdited: true } : {}),
    };
    return applyAutoDurationToDraftRow(nextRow);
  }));
  markRowDirty(bookingId);
  clearRowError(bookingId, field);
  setBulkSaveFeedback({ type: '', text: '' });

  if (selectedBookingId && String(bookingId) === String(selectedBookingId)) {
    setSelectedFlightDraft((prev) => {
      if (!prev) return prev;
      const nextRow = {
        ...prev,
        [field]: normalizedValue,
        ...(field === 'seDayPic' ? { seDayPicUserEdited: true } : {}),
      };
      return applyAutoDurationToDraftRow(nextRow);
    });
    if (selectedFlightFeedback.text) {
      setSelectedFlightFeedback({ type: '', text: '' });
    }
  }
}

function updateUtcLogbookCell(bookingId, field, value) {
  const textValue = String(value || '').toUpperCase();

  setRows((prev) => prev.map((row) => {
    if (String(row.bookingId) !== String(bookingId)) return row;
    const nextRow = {
      ...row,
      [field]: textValue,
      seDayPicUserEdited: false,
    };
    return applyAutoDurationToDraftRow(nextRow);
  }));
  markRowDirty(bookingId);
  clearRowError(bookingId, field);
  clearRowError(bookingId, 'seDayPic');
  setBulkSaveFeedback({ type: '', text: '' });

  if (selectedBookingId && String(bookingId) === String(selectedBookingId)) {
    setSelectedFlightDraft((prev) => {
      if (!prev) return prev;
      const nextRow = {
        ...prev,
        [field]: textValue,
        seDayPicUserEdited: false,
      };
      return applyAutoDurationToDraftRow(nextRow);
    });
  }

  const errorKey = getUtcFieldErrorKey(bookingId, field);
  setUtcFieldErrors((prev) => {
    if (!prev[errorKey]) return prev;
    const next = { ...prev };
    delete next[errorKey];
    return next;
  });
}

function commitUtcLogbookCell(bookingId, field) {
  const currentRow = rows.find((row) => String(row.bookingId) === String(bookingId));
  const rawValue = currentRow?.[field] || '';
  const normalized = normalizeUtcClockValue(rawValue);
  const errorKey = getUtcFieldErrorKey(bookingId, field);

  if (!normalized.ok) {
    setUtcFieldErrors((prev) => ({ ...prev, [errorKey]: normalized.error }));
    return false;
  }

  setUtcFieldErrors((prev) => {
    if (!prev[errorKey]) return prev;
    const next = { ...prev };
    delete next[errorKey];
    return next;
  });

  setRows((prev) => prev.map((row) => {
    if (String(row.bookingId) !== String(bookingId)) return row;
    const nextRow = {
      ...row,
      [field]: normalized.value,
      seDayPicUserEdited: false,
    };
    return applyAutoDurationToDraftRow(nextRow);
  }));
  markRowDirty(bookingId);
  clearRowError(bookingId, field);
  clearRowError(bookingId, 'seDayPic');

  if (selectedBookingId && String(bookingId) === String(selectedBookingId)) {
    setSelectedFlightDraft((prev) => {
      if (!prev) return prev;
      const nextRow = {
        ...prev,
        [field]: normalized.value,
        seDayPicUserEdited: false,
      };
      return applyAutoDurationToDraftRow(nextRow);
    });
  }

  return true;
}

function commitHourLogbookCell(bookingId, field) {
  const currentRow = rows.find((row) => String(row.bookingId) === String(bookingId));
  if (!currentRow) return false;

  const rawValue = field === 'seDayPic'
    ? String(currentRow.seDayPic || '').trim()
    : String(currentRow[field] || '').trim();

  if (!rawValue) {
    clearRowError(bookingId, field);
    return true;
  }

  const normalizedDisplay = formatHourValueForDisplay(rawValue, pilotTimeFormat);
  if (!normalizedDisplay || !isValidHourValueForSelectedFormat(normalizedDisplay, pilotTimeFormat)) {
    const rowNumber = getDisplayRowNumber(bookingId);
    const fieldLabel = hourFieldLabels[field] || field;
    const formatHelp = pilotTimeFormat === LOGBOOK_TIME_FORMAT_HOURS_MINUTES
      ? 'H:MM'
      : `decimal hours (${LOGBOOK_DECIMAL_PLACES} dp)`;
    const message = rowNumber
      ? `Row ${rowNumber}, ${fieldLabel}: use ${formatHelp}.`
      : `${fieldLabel}: use ${formatHelp}.`;
    setRowFieldErrors((prev) => ({
      ...prev,
      [getUtcFieldErrorKey(bookingId, field)]: 'Invalid hour value.',
    }));
    setBulkSaveFeedback({ type: 'error', text: message });
    return false;
  }

  setRows((prev) => prev.map((row) => {
    if (String(row.bookingId) !== String(bookingId)) return row;
    return {
      ...row,
      [field]: normalizedDisplay,
      ...(field === 'seDayPic' ? { seDayPicUserEdited: true } : {}),
    };
  }));

  if (selectedBookingId && String(bookingId) === String(selectedBookingId)) {
    setSelectedFlightDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: normalizedDisplay,
        ...(field === 'seDayPic' ? { seDayPicUserEdited: true } : {}),
      };
    });
  }

  clearRowError(bookingId, field);
  markRowDirty(bookingId);
  return true;
}

function handleEligibleAllocationCellFocus(bookingId, field, event) {
  if (!isEligibleAllocationField(field)) return;

  const currentRow = rows.find((row) => String(row.bookingId) === String(bookingId));
  if (!currentRow) return;

  const remainderValue = getAutoFillRemainderValue(currentRow, pilotTimeFormat, field);
  if (!remainderValue) return;

  const targetInput = event?.currentTarget;
  if (targetInput) {
    targetInput.value = remainderValue;
    if (typeof targetInput.setSelectionRange === 'function') {
      try {
        targetInput.setSelectionRange(0, remainderValue.length);
      } catch (_error) {
        // Ignore browsers that do not support selection on this input type.
      }
    }
  }

  setRows((prev) => prev.map((row) => {
    if (String(row.bookingId) !== String(bookingId)) return row;
    return {
      ...row,
      [field]: remainderValue,
      ...(field === 'seDayPic' ? { seDayPicUserEdited: true } : {}),
    };
  }));

  if (selectedBookingId && String(bookingId) === String(selectedBookingId)) {
    setSelectedFlightDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: remainderValue,
        ...(field === 'seDayPic' ? { seDayPicUserEdited: true } : {}),
      };
    });
  }

  markRowDirty(bookingId);
  clearRowError(bookingId, field);
  setBulkSaveFeedback({ type: '', text: '' });
}

function commitDateLogbookCell(bookingId) {
  const currentRow = rows.find((row) => String(row.bookingId) === String(bookingId));
  if (!currentRow) return false;

  const dateResult = normalizeLogbookDateInput(currentRow.date || '');
  if (!dateResult.ok) {
    setRowFieldErrors((prev) => ({
      ...prev,
      [getUtcFieldErrorKey(bookingId, 'date')]: dateResult.error || 'Enter date as DD/MM/YYYY.',
    }));
    return false;
  }

  setRows((prev) => prev.map((row) => (
    String(row.bookingId) === String(bookingId)
      ? { ...row, date: dateResult.display }
      : row
  )));

  if (selectedBookingId && String(bookingId) === String(selectedBookingId)) {
    setSelectedFlightDraft((prev) => (prev ? { ...prev, date: dateResult.display } : prev));
  }

  clearRowError(bookingId, 'date');
  markRowDirty(bookingId);
  return true;
}

function formatLogbookValue(value) {
  if (typeof value !== 'string') return value;

  let formatted = value.trim();

  if (formatted.startsWith('.')) {
    formatted = `0${formatted}`;
  }

  if (/^\d+$/.test(formatted)) {
    formatted = `${formatted}.0`;
  }

  return formatted;
}

const hourFieldKeySet = new Set([
  'seDayDual',
  'seDayPic',
  'seNightDual',
  'seNightPic',
  'meDayDual',
  'meDayPic',
  'meDayCopilot',
  'meDayPiUs',
  'meNightDual',
  'meNightPic',
  'meNightCopilot',
  'meNightPiUs',
  'instrumentFlight',
  'otherFlying',
]);

async function markBookingLogbookComplete(bookingId) {
  if (!bookingId) return false;

  const completedAt = new Date().toISOString();
  const updateAttempts = [
    {
      logbook_status: 'completed',
      logbook_completed_at: completedAt,
      pilot_logbook_status: 'completed',
      pilot_logbook_completed_at: completedAt,
      pilot_logbook_saved_at: completedAt,
      pilot_logbook_saved: true,
    },
    {
      logbook_status: 'completed',
      logbook_completed_at: completedAt,
    },
    {
      pilot_logbook_status: 'completed',
      pilot_logbook_completed_at: completedAt,
    },
    {
      pilot_logbook_saved_at: completedAt,
      pilot_logbook_saved: true,
    },
  ];

  // Not scoped by user_id: an organisation owner/admin may legitimately
  // complete another pilot's booking here. Database authorization (RLS)
  // remains responsible for who is allowed to update this row.
  const targetBookingId = String(bookingId);

  for (let i = 0; i < updateAttempts.length; i += 1) {
    const { data: updatedRows, error: updateError } = await supabase
      .from('bookings')
      .update(updateAttempts[i])
      .eq('id', bookingId)
      .select('id');

    if (!updateError) {
      const matchedIntendedRow = (updatedRows || []).some(
        (row) => String(row?.id) === targetBookingId
      );

      if (matchedIntendedRow) {
        return true;
      }

      setSelectedFlightFeedback({
        type: 'error',
        text: 'Logbook entry saved, but the booking record could not be confirmed as updated. Reload and try again.',
      });
      return false;
    }

    const message = String(updateError?.message || '').toLowerCase();
    const missingColumn = message.includes('column') || message.includes('schema cache');
    if (!missingColumn) {
      setSelectedFlightFeedback({
        type: 'error',
        text: `Logbook entry saved, but workflow status was not updated: ${updateError.message}`,
      });
      return false;
    }
  }

  return false;
}

// Recovery action for a persistent mismatch: a genuine Pilot Logbook entry is
// saved, but the booking was never marked complete (e.g. left over from
// before atomic save+complete existed, or any other out-of-band edit). Reuses
// markBookingLogbookComplete() directly rather than re-deriving its retry logic.
async function handleRetryBookingCompletion() {
  if (!selectedBookingId || retryingBookingCompletion) return;

  setRetryingBookingCompletion(true);
  setSelectedFlightFeedback({ type: '', text: '' });

  const completed = await markBookingLogbookComplete(selectedBookingId);

  setRetryingBookingCompletion(false);

  if (completed) {
    setSelectedBookingCompletionMismatch(false);
    setSelectedFlightFeedback({
      type: 'success',
      text: 'Booking marked complete.',
    });
  }
}

async function saveLogbookRow(rowToSave) {
  const row = rows.find((item) => String(item.bookingId) === String(rowToSave?.bookingId)) || rowToSave;
const normalizedRow = Object.fromEntries(
  Object.entries(row).map(([key, value]) => [
    key,
    hourFieldKeySet.has(key) ? normalizeHourValueForSave(value) : formatLogbookValue(value),
  ])
);
if (!user?.id || !row?.bookingId) {
  return { ok: false, message: 'Missing user or booking.' };
}

  const isBookingLinkedRow =
    Boolean(row.bookingId) &&
    !row.isManual &&
    !row.isManualDraft;

  if (
    isBookingLinkedRow &&
    !isBookingLinkedPilotLogbookEligible(row)
  ) {
    return {
      ok: false,
      message: 'Pilot Logbook entries require completed Post Flight / Tech Log.',
    };
  }

  normalizedRow.seDayDual = resolveSeDayDualValueForSave(row);
  normalizedRow.seDayPic = resolveSeDayPicValueForSave(row);

  const dateCheck = normalizeLogbookDateInput(normalizedRow.date || '');
  if (!dateCheck.ok) {
    return { ok: false, message: dateCheck.error || 'Enter date as DD/MM/YYYY.' };
  }
  normalizedRow.date = dateCheck.display;

  const depUtcCheck = normalizeUtcClockValue(normalizedRow.depUtc || '');
  if (!depUtcCheck.ok) {
    return { ok: false, message: depUtcCheck.error };
  }

  const arrUtcCheck = normalizeUtcClockValue(normalizedRow.arrUtc || '');
  if (!arrUtcCheck.ok) {
    return { ok: false, message: arrUtcCheck.error };
  }

  normalizedRow.depUtc = depUtcCheck.value;
  normalizedRow.arrUtc = arrUtcCheck.value;

  const bookingKey = String(row.bookingId);
  const existingEntry = techLogMap[bookingKey] || {};
  const existingPilotLogbook = existingEntry.pilotLogbook || {};

  const nextEntry = {
  ...existingEntry,
  pilotLogbook: {
    ...existingPilotLogbook,

    date: dateCheck.storage || '',
    type: normalizedRow.aircraftType || '',
    reg: normalizedRow.aircraftRegistration || '',

    pilot: normalizedRow.pilot || '',
    student: normalizedRow.student || '',
    to: normalizedRow.to || '',
    from: normalizedRow.from || '',

    depUtc: normalizedRow.depUtc || '',
    arrUtc: normalizedRow.arrUtc || '',
    takeoffTime: normalizedRow.depUtc || '',
    landingTime: normalizedRow.arrUtc || '',

    seDayDual: normalizedRow.seDayDual || '',
    seDayPic: normalizedRow.seDayPic || '',
    isTrainingFlight: Boolean(row.isTrainingFlight),
    seNightDual: normalizedRow.seNightDual || '',
    seNightPic: normalizedRow.seNightPic || '',

    meDayDual: normalizedRow.meDayDual || '',
    meDayPic: normalizedRow.meDayPic || '',
    meDayCopilot: normalizedRow.meDayCopilot || '',
    meDayPiUs: normalizedRow.meDayPiUs || '',

    meNightDual: normalizedRow.meNightDual || '',
    meNightPic: normalizedRow.meNightPic || '',
    meNightCopilot: normalizedRow.meNightCopilot || '',
    meNightPiUs: normalizedRow.meNightPiUs || '',

    instrumentFlight: normalizedRow.instrumentFlight || '',
    otherFlying: normalizedRow.otherFlying || '',

    notes: normalizedRow.notes || '',
  },
};

  setSavingRowId(bookingKey);

  const expectedVersion = Object.prototype.hasOwnProperty.call(techLogVersionMap, bookingKey)
    ? techLogVersionMap[bookingKey]
    : undefined;

  // Eligible booking-linked rows save the log and complete the booking atomically;
  // manual/paper/ineligible rows keep using the generic save-only RPC.
  const shouldSaveAtomically =
    isEligibleBookingLinkedLogbookRow(row) && isBookingLinkedPilotLogbookEligible(row);

  const result = shouldSaveAtomically
    ? await saveOfficialTechLogEntryAndCompleteBooking({
      bookingId: bookingKey,
      entry: nextEntry,
      expectedVersion,
    })
    : await saveOfficialTechLogEntry({
      bookingId: bookingKey,
      userId: user.id,
      entry: nextEntry,
      expectedVersion,
    });

  setSavingRowId('');

  if (!result.ok) {
    const isConflict = String(result.message || '').toLowerCase().includes('updated this tech log first');
    const message = isConflict
      ? 'This Tech Log changed elsewhere. Reload, review the latest entry, and save again.'
      : (result.message || 'Could not save pilot logbook changes.');
    setError(message);
    return { ok: false, message, conflict: isConflict };
  }

  setTechLogMap((prev) => ({
    ...prev,
    [bookingKey]: nextEntry,
  }));
  setTechLogVersionMap((prev) => ({
    ...prev,
    [bookingKey]: result.version,
  }));

  const normalizedResultRow = {
    ...normalizedRow,
    bookingId: bookingKey,
    aircraftId: String(normalizedRow.aircraftId || ''),
    savedAt: new Date().toISOString(),
    techLogStatus: String(normalizedRow.techLogStatus || ''),
    isManual: Boolean(normalizedRow.isManual),
    isManualDraft: false,
    isDraft: false,
  };

  setRows((prev) => {
    const existingIndex = prev.findIndex((item) => String(item.bookingId) === bookingKey);
    if (existingIndex === -1) {
      return [...prev, normalizedResultRow];
    }

    const next = [...prev];
    next[existingIndex] = { ...next[existingIndex], ...normalizedResultRow };
    return next;
  });

  return { ok: true, entry: nextEntry };
}

function isValidHourValueForSelectedFormat(value, timeFormat) {
  const text = String(value || '').trim();
  if (!text) return true;

  if (normalizePilotTimeFormat(timeFormat) === LOGBOOK_TIME_FORMAT_HOURS_MINUTES) {
    const match = text.match(/^(\d{1,3}):(\d{2})$/);
    if (!match) return false;
    const minutes = Number(match[2]);
    return Number.isInteger(minutes) && minutes >= 0 && minutes <= 59;
  }

  const decimalPattern = new RegExp(`^\\d+(?:[\\.,]\\d{1,${LOGBOOK_DECIMAL_PLACES}})?$`);
  return decimalPattern.test(text);
}

function validateRowForSave(row, rowIndex) {
  const dateCheck = normalizeLogbookDateInput(row?.date || '');
  if (!dateCheck.ok) {
    return {
      ok: false,
      field: 'date',
      message: `Row ${rowIndex + 1} Date: ${dateCheck.error || 'Enter date as DD/MM/YYYY.'}`,
    };
  }

  const depCheck = normalizeUtcClockValue(row?.depUtc || '');
  if (!depCheck.ok) {
    return {
      ok: false,
      field: 'depUtc',
      message: `Row ${rowIndex + 1} Departure UTC: ${depCheck.error}`,
    };
  }

  const arrCheck = normalizeUtcClockValue(row?.arrUtc || '');
  if (!arrCheck.ok) {
    return {
      ok: false,
      field: 'arrUtc',
      message: `Row ${rowIndex + 1} Arrival UTC: ${arrCheck.error}`,
    };
  }

  const allocationSummary = calculateAllocationSummaryForRow(row, pilotTimeFormat);
  if (allocationSummary.hasDuration && allocationSummary.isOverAllocated) {
    const overByText = formatDurationMinutesForDisplay(Math.abs(allocationSummary.remainingMinutes), pilotTimeFormat);
    return {
      ok: false,
      field: 'seDayPic',
      message: `Row ${rowIndex + 1}: allocated time exceeds flight duration by ${overByText}.`,
    };
  }

  for (let i = 0; i < hourFieldKeys.length; i += 1) {
    const field = hourFieldKeys[i];
    const rawValue = String(row?.[field] || '').trim();
    const candidate = field === 'seDayPic'
      ? resolveSeDayPicValueForDisplay(row, pilotTimeFormat)
      : formatHourValueForDisplay(rawValue, pilotTimeFormat);

    if (rawValue && !candidate) {
      return {
        ok: false,
        field,
        message: `Row ${rowIndex + 1} ${field}: invalid time value.`,
      };
    }

    if (!isValidHourValueForSelectedFormat(candidate, pilotTimeFormat)) {
      return {
        ok: false,
        field,
        message: `Row ${rowIndex + 1} ${field}: use ${pilotTimeFormat === LOGBOOK_TIME_FORMAT_HOURS_MINUTES ? 'H:MM' : `decimal hours (${LOGBOOK_DECIMAL_PLACES} dp)`}.`,
      };
    }
  }

  return { ok: true };
}

async function handleSaveLogbookChanges() {
  if (!user?.id || savingAllChanges) return;

  if (hasOverAllocatedRows) {
    return;
  }

  const changedIds = Object.keys(dirtyRowIds);
  if (changedIds.length === 0) {
    setBulkSaveFeedback({ type: 'success', text: 'No unsaved logbook changes.' });
    return;
  }

  setSavingAllChanges(true);
  setBulkSaveFeedback({ type: '', text: '' });
  setRowFieldErrors({});
  setError('');

  const errorAccumulator = {};
  const savedRowIds = new Set();
  const saveFailures = [];

  for (let i = 0; i < changedIds.length; i += 1) {
    const bookingId = String(changedIds[i]);
    const rowIndex = rows.findIndex((row) => String(row.bookingId) === bookingId);
    const row = rowIndex >= 0 ? rows[rowIndex] : null;
    if (!row) continue;

    const validation = validateRowForSave(row, rowIndex);
    if (!validation.ok) {
      const errorKey = getUtcFieldErrorKey(bookingId, validation.field);
      errorAccumulator[errorKey] = validation.message;
      setSavingAllChanges(false);
      setRowFieldErrors(errorAccumulator);
      setBulkSaveFeedback({ type: 'error', text: validation.message });
      return;
    }

    const saveResult = await saveLogbookRow(row);
    if (!saveResult?.ok) {
      saveFailures.push({
        bookingId,
        message: saveResult?.message || `Could not save row ${rowIndex + 1}.`,
      });
      continue;
    }
    savedRowIds.add(bookingId);

    if (selectedBookingId && bookingId === String(selectedBookingId)) {
      setSelectedFlightHasExistingEntry(true);
    }
  }

  setSavingAllChanges(false);
  setRows((prev) => sortPilotLogbookRowsForDisplay(prev, {}));
  setDirtyRowIds((previous) => {
    const next = { ...previous };
    savedRowIds.forEach((bookingId) => {
      delete next[bookingId];
    });
    return next;
  });

  const feedbackParts = [];
  if (savedRowIds.size > 0) {
    feedbackParts.push(savedRowIds.size === 1 ? '1 row saved.' : `${savedRowIds.size} rows saved.`);
  }
  if (saveFailures.length > 0) {
    feedbackParts.push(`Could not save booking-linked rows: ${saveFailures.map((failure) => failure.bookingId).join(', ')}.`);
  }

  setBulkSaveFeedback({
    type: saveFailures.length > 0 ? 'warning' : 'success',
    text: feedbackParts.join(' '),
  });
}

function toggleManualRowSelection(bookingId, isChecked) {
  const key = String(bookingId || '');
  if (!key) return;

  setSelectedManualRowIds((prev) => {
    if (isChecked) {
      return { ...prev, [key]: true };
    }

    if (!prev[key]) return prev;
    const next = { ...prev };
    delete next[key];
    return next;
  });
}

function handleRequestDeleteSelected() {
  if (!hasSelectedManualRows || deletingSelectedManualRows) return;
  setShowDeleteSelectedConfirm(true);
}

function handleCancelDeleteSelected() {
  if (deletingSelectedManualRows) return;
  setShowDeleteSelectedConfirm(false);
}

async function handleConfirmDeleteSelected() {
  if (!hasSelectedManualRows || deletingSelectedManualRows) return;

  const manualIdsToDelete = selectedManualIds.filter((bookingId) => {
    const row = rows.find((item) => String(item.bookingId) === String(bookingId));
    return isSelectableManualRow(row);
  });

  if (manualIdsToDelete.length === 0) {
    setShowDeleteSelectedConfirm(false);
    setSelectedManualRowIds({});
    return;
  }

  setDeletingSelectedManualRows(true);
  setBulkSaveFeedback({ type: '', text: '' });

  const { error: deleteError } = await supabase
    .from('official_tech_logs')
    .delete()
    .in('booking_id', manualIdsToDelete);

  setDeletingSelectedManualRows(false);

  if (deleteError) {
    setBulkSaveFeedback({
      type: 'error',
      text: deleteError.message || 'Could not delete selected manual logbook entries.',
    });
    return;
  }

  const deleteIdSet = new Set(manualIdsToDelete.map((id) => String(id)));

  setRows((prev) => sortPilotLogbookRowsForDisplay(
    prev.filter((row) => !deleteIdSet.has(String(row.bookingId))),
    {}
  ));
  setTechLogMap((prev) => {
    const next = { ...prev };
    manualIdsToDelete.forEach((bookingId) => {
      delete next[String(bookingId)];
    });
    return next;
  });
  setDirtyRowIds((prev) => {
    const next = { ...prev };
    manualIdsToDelete.forEach((bookingId) => {
      delete next[String(bookingId)];
    });
    return next;
  });
  setSelectedManualRowIds({});
  setShowDeleteSelectedConfirm(false);

  setBulkSaveFeedback({
    type: 'success',
    text: manualIdsToDelete.length === 1
      ? '1 manual entry deleted.'
      : `${manualIdsToDelete.length} manual entries deleted.`,
  });
}

function getManualDraftRow() {
  return rows.find((row) => row.isManualDraft);
}

function handleAddManualFlight() {
  if (!user?.id) return;
  if (getManualDraftRow()) return;

  const manualBookingId = buildManualEntryKey(user.id);

  if (!manualBookingId) {
    setBulkSaveFeedback({ type: 'error', text: 'Could not start manual entry.' });
    return;
  }

  const draftRow = {
    bookingId: manualBookingId,
    aircraftId: '',
    date: '',
    time: '',
    pilot: 'SELF',
    student: '',
    to: '',
    from: '',
    depUtc: '',
    arrUtc: '',
    seDayDual: '',
    seDayPic: '',
    seNightDual: '',
    seNightPic: '',
    meDayDual: '',
    meDayPic: '',
    meDayCopilot: '',
    meDayPiUs: '',
    meNightDual: '',
    meNightPic: '',
    meNightCopilot: '',
    meNightPiUs: '',
    instrumentFlight: '',
    otherFlying: '',
    flightTime: 0,
    taxiTime: 0,
    totalTime: 0,
    autoDurationMinutes: null,
    seDayPicUserEdited: false,
    notes: '',
    savedAt: '',
    techLogStatus: 'manual',
    aircraftType: '',
    aircraftRegistration: '',
    isManual: true,
    isDraft: true,
    isManualDraft: true,
  };

  setRows((prev) => [...prev, applyAutoDurationToDraftRow(draftRow)]);
  markRowDirty(manualBookingId);
  setBulkSaveFeedback({ type: '', text: '' });
}

function handleCancelManualEntry() {
  const draftRow = getManualDraftRow();
  if (draftRow?.bookingId) {
    setDirtyRowIds((prev) => {
      const key = String(draftRow.bookingId);
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }
  setRows((prev) => prev.filter((row) => !row.isManualDraft));
  setBulkSaveFeedback({ type: '', text: '' });
}

async function handlePilotTimeFormatChange(nextFormatValue) {
  const nextFormat = normalizePilotTimeFormat(nextFormatValue);
  setPilotTimeFormat(nextFormat);
  setPaperLogDraft((prev) => ({ ...prev, timeFormat: nextFormat }));
  setPilotTimeFormatFeedback({ type: '', text: '' });

  if (!user?.id || savingPilotTimeFormat) {
    return;
  }

  setSavingPilotTimeFormat(true);
  const paperLogKey = getPilotPaperLogKey(user.id);
  const existingEntry = techLogMap[paperLogKey] || {};
  const existingPaper = existingEntry.pilotLogbookPaper || existingEntry.pilotLogbook || existingEntry;

  const nextPaperDraft = buildPaperPilotDraft({
    ...existingPaper,
    ...paperLogDraft,
    timeFormat: nextFormat,
  });

  const nextEntry = {
    ...existingEntry,
    pilotLogbookPaper: nextPaperDraft,
  };

  const result = await saveOfficialTechLogEntry({
    bookingId: paperLogKey,
    userId: user.id,
    entry: nextEntry,
    expectedVersion: undefined,
  });

  setSavingPilotTimeFormat(false);

  if (!result.ok) {
    setPilotTimeFormatFeedback({
      type: 'error',
      text: result.message || 'Could not save logbook time format preference.',
    });
    return;
  }

  setTechLogMap((prev) => ({
    ...prev,
    [paperLogKey]: nextEntry,
  }));

  setShowLogbookSettings(false);

  setPilotTimeFormatFeedback({
    type: 'success',
    text: 'Logbook time format saved.',
  });
}

  function updatePaperBroughtForwardCell(field, value) {
  let normalizedValue = typeof value === 'string' ? value.toUpperCase() : value;

  if (typeof normalizedValue === 'string' && normalizedValue.startsWith('.')) {
    normalizedValue = `0${normalizedValue}`;
  }

  const nextDraft = {
    ...paperLogDraft,
    broughtForwardTimes: {
      ...(paperLogDraft.broughtForwardTimes || {}),
      [field]: normalizedValue,
    },
  };

  setPaperLogDraft(nextDraft);

  if (initialHoursFeedback.text) {
    setInitialHoursFeedback({ type: '', text: '' });
  }
}

async function handleSaveInitialHours(
  draftOverride = null
) {
  if (!user?.id || savingInitialHours) return;

  setSavingInitialHours(true);
  setInitialHoursFeedback({
    type: '',
    text: '',
  });
  setError('');

  const paperLogKey = getPilotPaperLogKey(user.id);
  const existingEntry = techLogMap[paperLogKey] || {};

  const nextDraft = buildPaperPilotDraft(
    draftOverride || paperLogDraft
  );

  const normalizedBroughtForwardTimes =
    hourFieldKeys.reduce((accumulator, field) => {
      const rawValue = String(
        nextDraft.broughtForwardTimes?.[field] || ''
      ).trim();

      if (rawValue) {
        const displayValue =
          formatHourValueForDisplay(
            rawValue,
            pilotTimeFormat
          );

        if (
          !displayValue
          || !isValidHourValueForSelectedFormat(
            displayValue,
            pilotTimeFormat
          )
        ) {
          setSavingInitialHours(false);
          setInitialHoursFeedback({
            type: 'error',
            text:
              `${hourFieldLabels[field]}: enter ${
                pilotTimeFormat
                  === LOGBOOK_TIME_FORMAT_HOURS_MINUTES
                  ? 'hours as H:MM'
                  : 'decimal hours'
              }.`,
          });
          return null;
        }
      }

      accumulator[field] =
        normalizeHourValueForSave(rawValue);

      return accumulator;
    }, {});

  if (!normalizedBroughtForwardTimes) {
    return;
  }

  nextDraft.initialHoursConfirmed = true;
  nextDraft.broughtForwardTimes =
    normalizedBroughtForwardTimes;

  const nextEntry = {
    ...existingEntry,
    pilotLogbookPaper: nextDraft,
  };

  const result = await saveOfficialTechLogEntry({
    bookingId: paperLogKey,
    userId: user.id,
    entry: nextEntry,
    expectedVersion: undefined,
  });

  setSavingInitialHours(false);

  if (!result.ok) {
    setInitialHoursFeedback({
      type: 'error',
      text:
        result.message
        || 'Could not save brought forward totals.',
    });
    return;
  }

  setTechLogMap((previous) => ({
    ...previous,
    [paperLogKey]: nextEntry,
  }));

  setPaperLogDraft(nextDraft);
  setInitialHoursSaved(true);

  setInitialHoursFeedback({
    type: '',
    text: '',
  });
}

async function handleConfirmNoPreviousHours() {
  const emptyBroughtForwardTimes =
    hourFieldKeys.reduce((accumulator, field) => {
      accumulator[field] = '';
      return accumulator;
    }, {});

  await handleSaveInitialHours({
    ...paperLogDraft,
    initialHoursConfirmed: true,
    broughtForwardTimes:
      emptyBroughtForwardTimes,
  });
}

  function handleDownloadPdf() {
  const numericFields = [
    'seDayDual',
    'seDayPic',
    'seNightDual',
    'seNightPic',
    'meDayDual',
    'meDayPic',
    'meDayCopilot',
    'meDayPiUs',
    'meNightDual',
    'meNightPic',
    'meNightCopilot',
    'meNightPiUs',
    'instrumentFlight',
    'otherFlying',
  ];

  const toNumber = (value) => {
    return parseHourValueToNumber(value);
  };

  const formatTotal = (value) => {
    return formatHourNumberForDisplay(value, pilotTimeFormat);
  };
const buildPdfRow = (row, normalizedRow) => ({
  ...row,
  ...normalizedRow,
  seDayPic: resolveSeDayPicValueForSave(row),
});
  const addTotals = (base, row) => {
    const next = { ...base };
    numericFields.forEach((field) => {
      next[field] = toNumber(next[field]) + toNumber(row[field]);
    });
    return next;
  };

  const broughtForward = paperLogDraft.broughtForwardTimes || {};
  let runningTotals = numericFields.reduce((acc, field) => {
    acc[field] = toNumber(broughtForward[field]);
    return acc;
  }, {});

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const rowsPerPage = 18;
  const pageChunks = [];

  for (let i = 0; i < displayRows.length; i += rowsPerPage) {
    pageChunks.push(displayRows.slice(i, i + rowsPerPage));
  }

  if (pageChunks.length === 0) {
    pageChunks.push([]);
  }

  const headRows = [
    [
      { content: 'DATE', rowSpan: 3 },
      { content: 'TYPE', rowSpan: 3 },
      { content: 'REG', rowSpan: 3 },
      { content: 'PILOT', rowSpan: 3 },
      { content: 'STUDENT', rowSpan: 3 },
      { content: 'TO', rowSpan: 3 },
      { content: 'FROM', rowSpan: 3 },
      { content: 'DEP\nUTC', rowSpan: 3 },
      { content: 'ARR\nUTC', rowSpan: 3 },
      { content: 'SINGLE-ENGINED AIRCRAFT', colSpan: 4 },
      { content: 'MULTI-ENGINED AIRCRAFT', colSpan: 8 },
      { content: 'INST\nFLIGHT\n(13)', rowSpan: 3 },
      { content: 'OTHER\nFLYING\n(14)', rowSpan: 3 },
      { content: 'REMARKS\n(15)', rowSpan: 3 },
    ],
    [
      { content: 'DAY', colSpan: 2 },
      { content: 'NIGHT', colSpan: 2 },
      { content: 'DAY', colSpan: 4 },
      { content: 'NIGHT', colSpan: 4 },
    ],
    [
      'DUAL\n(1)',
      'PIC\n(2)',
      'DUAL\n(3)',
      'PIC\n(4)',
      'DUAL\n(5)',
      'PIC\n(6)',
      'CO-PILOT\n(7)',
      'PI US\n(8)',
      'DUAL\n(9)',
      'PIC\n(10)',
      'CO-PILOT\n(11)',
      'PI US\n(12)',
    ],
  ];

  pageChunks.forEach((chunk, pageIndex) => {
    if (pageIndex > 0) {
      doc.addPage('a4', 'landscape');
    }

    const pageStartTotals = { ...runningTotals };

    const bodyRows = [];

    bodyRows.push([
      { content: 'TOTALS BROUGHT FORWARD', colSpan: 9, styles: { halign: 'left', fontStyle: 'bold' } },
      formatTotal(pageStartTotals.seDayDual),
      formatTotal(pageStartTotals.seDayPic),
      formatTotal(pageStartTotals.seNightDual),
      formatTotal(pageStartTotals.seNightPic),
      formatTotal(pageStartTotals.meDayDual),
      formatTotal(pageStartTotals.meDayPic),
      formatTotal(pageStartTotals.meDayCopilot),
      formatTotal(pageStartTotals.meDayPiUs),
      formatTotal(pageStartTotals.meNightDual),
      formatTotal(pageStartTotals.meNightPic),
      formatTotal(pageStartTotals.meNightCopilot),
      formatTotal(pageStartTotals.meNightPiUs),
      formatTotal(pageStartTotals.instrumentFlight),
      formatTotal(pageStartTotals.otherFlying),
      '',
    ]);

    chunk.forEach((row) => {
      const aircraft = aircraftMap[row.aircraftId] || {};
      const normalizedRow = normalizeLogbookRowForDisplay(row, aircraft);
      const pdfRow = buildPdfRow(row, normalizedRow);
runningTotals = addTotals(runningTotals, pdfRow);

      bodyRows.push([
        formatLogbookPdfDate(pdfRow.date),
        String(pdfRow.aircraftType || '').toUpperCase(),
        String(pdfRow.aircraftRegistration || '').toUpperCase(),
        String(pdfRow.pilot || '').toUpperCase(),
        String(pdfRow.student || '').toUpperCase(),
        String(pdfRow.to || '').toUpperCase(),
        String(pdfRow.from || '').toUpperCase(),
        formatTimeWithColon(pdfRow.depUtc),
        formatTimeWithColon(pdfRow.arrUtc),
        formatHourValueForDisplay(pdfRow.seDayDual || '', pilotTimeFormat),
        formatHourValueForDisplay(pdfRow.seDayPic || '', pilotTimeFormat),
        formatHourValueForDisplay(pdfRow.seNightDual || '', pilotTimeFormat),
        formatHourValueForDisplay(pdfRow.seNightPic || '', pilotTimeFormat),
        formatHourValueForDisplay(pdfRow.meDayDual || '', pilotTimeFormat),
        formatHourValueForDisplay(pdfRow.meDayPic || '', pilotTimeFormat),
        formatHourValueForDisplay(pdfRow.meDayCopilot || '', pilotTimeFormat),
        formatHourValueForDisplay(pdfRow.meDayPiUs || '', pilotTimeFormat),
        formatHourValueForDisplay(pdfRow.meNightDual || '', pilotTimeFormat),
        formatHourValueForDisplay(pdfRow.meNightPic || '', pilotTimeFormat),
        formatHourValueForDisplay(pdfRow.meNightCopilot || '', pilotTimeFormat),
        formatHourValueForDisplay(pdfRow.meNightPiUs || '', pilotTimeFormat),
        formatHourValueForDisplay(pdfRow.instrumentFlight || '', pilotTimeFormat),
        formatHourValueForDisplay(pdfRow.otherFlying || '', pilotTimeFormat),
        String(pdfRow.notes || '').toUpperCase(),
      ]);
    });

    

    bodyRows.push([
      { content: 'TOTALS CARRIED FORWARD', colSpan: 9, styles: { halign: 'left', fontStyle: 'bold' } },
      formatTotal(runningTotals.seDayDual),
      formatTotal(runningTotals.seDayPic),
      formatTotal(runningTotals.seNightDual),
      formatTotal(runningTotals.seNightPic),
      formatTotal(runningTotals.meDayDual),
      formatTotal(runningTotals.meDayPic),
      formatTotal(runningTotals.meDayCopilot),
      formatTotal(runningTotals.meDayPiUs),
      formatTotal(runningTotals.meNightDual),
      formatTotal(runningTotals.meNightPic),
      formatTotal(runningTotals.meNightCopilot),
      formatTotal(runningTotals.meNightPiUs),
      formatTotal(runningTotals.instrumentFlight),
      formatTotal(runningTotals.otherFlying),
      '',
    ]);

    
doc.setDrawColor(210, 210, 210);
doc.setLineWidth(0.4);
doc.rect(8, 8, 826, 575);
    doc.setFontSize(15);
doc.setFont(undefined, 'bold');
doc.text('PILOT LOGBOOK', 14, 20);

doc.setFontSize(7);
doc.setFont(undefined, 'normal');

doc.setDrawColor(180, 180, 180);
doc.rect(14, 24, 814, 14);




doc.text(
  `Page ${pageIndex + 1} of ${pageChunks.length}`,
  808,
  33,
  { align: 'right' }
);






doc.setFontSize(6);
doc.setFont(undefined, 'normal');

doc.text(
  `Generated by Wingit • ${formatDateDisplay(new Date().toISOString().slice(0, 10))}`,
  14,
  575
);
doc.text(
  'Pilot logbook entries should be checked by the pilot before submission.',
  828,
  575,
  { align: 'right' }
);
    autoTable(doc, {
      head: headRows,
      body: bodyRows,
      startY: 52,
      theme: 'grid',
      margin: { left: 10, right: 10, top: 24, bottom: 14 },
      tableWidth: 'auto',
      styles: {
        fontSize: 5.8,
        cellPadding: 2.2,
        lineColor: [0, 0, 0],
        lineWidth: 0.45,
        valign: 'middle',
        halign: 'center',
        textColor: [0, 0, 0],
        overflow: 'linebreak',
      },
      headStyles: {
  fontSize: 6.2,
  fontStyle: 'bold',
  halign: 'center',
  valign: 'middle',
  fillColor: [240, 243, 246],
  textColor: [20, 20, 20],
},
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
   columnStyles: {
  0: { cellWidth: 38, halign: 'left' }, // Date
  1: { cellWidth: 23 },
  2: { cellWidth: 31 },
  3: { cellWidth: 62, halign: 'left' }, // Pilot
  4: { cellWidth: 62, halign: 'left' }, // Student
  5: { cellWidth: 52, halign: 'left' },
  6: { cellWidth: 52, halign: 'left' },
  7: { cellWidth: 25 },
  8: { cellWidth: 25 },

  9: { cellWidth: 27 },
  10: { cellWidth: 27 },
  11: { cellWidth: 27 },
  12: { cellWidth: 27 },
  13: { cellWidth: 27 },
  14: { cellWidth: 27 },
  15: { cellWidth: 28 },
  16: { cellWidth: 27 },
  17: { cellWidth: 27 },
  18: { cellWidth: 27 },
  19: { cellWidth: 28 },
  20: { cellWidth: 27 },

  21: { cellWidth: 30 },
  22: { cellWidth: 31 },
 23: { cellWidth: 58, halign: 'left' }, // Remarks},
},
      didParseCell: (data) => {
  const isTotalsRow = data.section === 'body' && (
    data.row.index === 0 ||
    data.row.index === bodyRows.length - 1
  );

  if (isTotalsRow) {
    data.cell.styles.fontStyle = 'bold';
    data.cell.styles.fillColor = [220, 226, 233];
data.cell.styles.lineWidth = 0.95;
data.cell.styles.fontSize = 6.1;
    
  }

  if (data.section === 'head') {
  data.cell.styles.fillColor = [244, 246, 248];
  data.cell.styles.fontStyle = 'bold';
}

if (data.section === 'head' && data.row.index === 0) {
  data.cell.styles.fillColor = [226, 232, 240];
  data.cell.styles.fontSize = 6.4;
}

  if (data.section === 'body' && data.column.index === 23) {
    data.cell.styles.halign = 'left';
  }
},
    });
  });

  doc.save(`pilot-logbook-${new Date().toISOString().slice(0, 10)}.pdf`);
}
  function renderBroughtForwardCell(
    field,
    ariaLabel,
    isPrimary = false
  ) {
    const editable =
      !initialHoursSaved
      && !initialHoursLocked;

    const rawValue = String(
      paperLogDraft.broughtForwardTimes?.[field]
      || ''
    );

    return (
      <td
        style={{
          ...tdStyle,
          ...(editable
            ? {
                background: '#fff4f4',
                boxShadow:
                  'inset 0 0 0 1px #c62828',
              }
            : {}),
        }}
      >
        {editable ? (
          <input
            className="pilot-logbook-cell-input"
            aria-label={ariaLabel}
            inputMode={
              pilotTimeFormat
                === LOGBOOK_TIME_FORMAT_HOURS_MINUTES
                ? 'text'
                : 'decimal'
            }
            value={rawValue}
            onChange={(event) =>
              updatePaperBroughtForwardCell(
                field,
                event.target.value
              )
            }
            style={{
              ...spreadsheetInputStyle,
              textAlign: 'center',
              fontWeight: 700,
              background: isPrimary
                ? '#fff1f1'
                : 'transparent',
            }}
          />
        ) : (
          formatHourValueForDisplay(
            rawValue,
            pilotTimeFormat
          )
        )}
      </td>
    );
  }
  const logbookStatisticsRows = displayRows
    .map((row) =>
      normalizeLogbookRowForDisplay(
        row,
        aircraftMap[String(row?.aircraftId || '')] || {}
      )
    )
    .filter((row) => {
    if (!row || row.isManualDraft) return false;

    if (
      isCancelledOrDidNotFlyStatus(row.techLogStatus)
      || isCancelledOrDidNotFlyNotes(row.notes)
    ) {
      return false;
    }

    return (
      hasOperationalFlightData(row)
      || hasAnyPositiveHourValue(row)
    );
  });

  const statisticsCapacityFields = [
    'seDayDual',
    'seDayPic',
    'seNightDual',
    'seNightPic',
    'meDayDual',
    'meDayPic',
    'meDayCopilot',
    'meDayPiUs',
    'meNightDual',
    'meNightPic',
    'meNightCopilot',
    'meNightPiUs',
  ];

  const statisticsPicFields = [
    'seDayPic',
    'seNightPic',
    'meDayPic',
    'meNightPic',
  ];

  const statisticsDualFields = [
    'seDayDual',
    'seNightDual',
    'meDayDual',
    'meNightDual',
  ];

  const statisticsCopilotFields = [
    'meDayCopilot',
    'meNightCopilot',
  ];

  const statisticsPiUsFields = [
    'meDayPiUs',
    'meNightPiUs',
  ];

  const statisticsNightFields = [
    'seNightDual',
    'seNightPic',
    'meNightDual',
    'meNightPic',
    'meNightCopilot',
    'meNightPiUs',
  ];

  const getStatisticsFieldMinutes = (
    source,
    fieldNames
  ) => fieldNames.reduce((total, fieldName) => {
    const fieldValue = fieldName === 'seDayPic'
      ? resolveSeDayPicValueForSave(source)
      : source?.[fieldName];

    const hours = toHoursNumber(fieldValue);

    return total + (
      Number.isFinite(hours)
        ? Math.round(hours * 60)
        : 0
    );
  }, 0);

  const getStatisticsRowMinutes = (row) => {
    const exactDuration =
      getRowFlightDurationMinutes(row);

    if (
      Number.isFinite(exactDuration)
      && exactDuration > 0
    ) {
      return exactDuration;
    }

    const allocatedMinutes =
      getStatisticsFieldMinutes(
        row,
        statisticsCapacityFields
      );

    if (allocatedMinutes > 0) {
      return allocatedMinutes;
    }

    const fallbackHours = toHoursNumber(
      row?.totalTime || row?.flightTime
    );

    return Number.isFinite(fallbackHours)
      ? Math.round(fallbackHours * 60)
      : 0;
  };

  const broughtForwardCapacityMinutes =
    getStatisticsFieldMinutes(
      paperLogDraft,
      statisticsCapacityFields
    );

  const electronicFlightMinutes =
    logbookStatisticsRows.reduce(
      (total, row) =>
        total + getStatisticsRowMinutes(row),
      0
    );

  const getCombinedStatisticsMinutes = (
    fieldNames
  ) => {
    const broughtForwardMinutes =
      getStatisticsFieldMinutes(
        paperLogDraft,
        fieldNames
      );

    const electronicMinutes =
      logbookStatisticsRows.reduce(
        (total, row) =>
          total
          + getStatisticsFieldMinutes(
            row,
            fieldNames
          ),
        0
      );

    return broughtForwardMinutes
      + electronicMinutes;
  };

  const picStatisticsMinutes =
    getCombinedStatisticsMinutes(
      statisticsPicFields
    );

  const dualStatisticsMinutes =
    getCombinedStatisticsMinutes(
      statisticsDualFields
    );

  const copilotStatisticsMinutes =
    getCombinedStatisticsMinutes(
      statisticsCopilotFields
    );

  const piUsStatisticsMinutes =
    getCombinedStatisticsMinutes(
      statisticsPiUsFields
    );

  const nightStatisticsMinutes =
    getCombinedStatisticsMinutes(
      statisticsNightFields
    );

  const instrumentStatisticsMinutes =
    getCombinedStatisticsMinutes([
      'instrumentFlight',
    ]);

  const totalStatisticsMinutes =
    broughtForwardCapacityMinutes
    + electronicFlightMinutes;

  const formatStatisticsMinutes = (minutes) => {
    const formatted =
      formatDurationMinutesForDisplay(
        minutes,
        pilotTimeFormat
      );

    if (formatted) return formatted;

    return pilotTimeFormat
      === LOGBOOK_TIME_FORMAT_HOURS_MINUTES
      ? '0:00'
      : '0.0';
  };

  const logbookStatisticsSummaryCards = [
    {
      label: 'Flights',
      value: logbookStatisticsRows.length,
    },
    {
      label: 'Total flying time',
      value: formatStatisticsMinutes(
        totalStatisticsMinutes
      ),
    },
    {
      label: 'Pilot in command',
      value: formatStatisticsMinutes(
        picStatisticsMinutes
      ),
    },
    {
      label: 'Dual',
      value: formatStatisticsMinutes(
        dualStatisticsMinutes
      ),
    },
    {
      label: 'Night',
      value: formatStatisticsMinutes(
        nightStatisticsMinutes
      ),
    },
    {
      label: 'Instrument',
      value: formatStatisticsMinutes(
        instrumentStatisticsMinutes
      ),
    },
  ];

  const statisticsMonthlyMap = new Map();

  logbookStatisticsRows.forEach((row) => {
    const dateText = String(row?.date || '').trim();
    let year = 0;
    let month = 0;

    const ukDateMatch = dateText.match(
      /^(\d{2})\/(\d{2})\/(\d{4})$/
    );

    if (ukDateMatch) {
      month = Number(ukDateMatch[2]);
      year = Number(ukDateMatch[3]);
    } else {
      const parsedDate = new Date(dateText);

      if (!Number.isNaN(parsedDate.getTime())) {
        month = parsedDate.getUTCMonth() + 1;
        year = parsedDate.getUTCFullYear();
      }
    }

    if (!year || !month) return;

    const key =
      `${year}-${String(month).padStart(2, '0')}`;

    const label = new Intl.DateTimeFormat(
      'en-GB',
      {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }
    ).format(
      new Date(Date.UTC(year, month - 1, 1))
    );

    const previous =
      statisticsMonthlyMap.get(key) || {
        key,
        label,
        minutes: 0,
      };

    previous.minutes +=
      getStatisticsRowMinutes(row);

    statisticsMonthlyMap.set(key, previous);
  });

  const logbookStatisticsMonthlyData =
    Array.from(statisticsMonthlyMap.values())
      .sort((first, second) =>
        first.key.localeCompare(second.key)
      )
      .slice(-12);

  const statisticsAircraftMap = new Map();

  logbookStatisticsRows.forEach((row) => {
    const label = String(
      row?.aircraftType
      || row?.type
      || 'Unknown type'
    )
      .trim()
      .toUpperCase();

    const previous =
      statisticsAircraftMap.get(label) || {
        label,
        minutes: 0,
      };

    previous.minutes +=
      getStatisticsRowMinutes(row);

    statisticsAircraftMap.set(
      label,
      previous
    );
  });

  const logbookStatisticsAircraftData =
    Array.from(statisticsAircraftMap.values())
      .sort(
        (first, second) =>
          second.minutes - first.minutes
      )
      .slice(0, 10);

  const logbookStatisticsCapacityData = [
    {
      label: 'Pilot in command',
      minutes: picStatisticsMinutes,
    },
    {
      label: 'Dual',
      minutes: dualStatisticsMinutes,
    },
    {
      label: 'Co-pilot',
      minutes: copilotStatisticsMinutes,
    },
    {
      label: 'P1 under supervision',
      minutes: piUsStatisticsMinutes,
    },
  ];
  const airportsVisitedRows =
    buildAirportsVisitedSummary(displayRows);

  const normalizedAirportSearch =
    airportSearch.trim().toUpperCase();

  const filteredAirportsVisitedRows =
    normalizedAirportSearch
      ? airportsVisitedRows.filter((entry) =>
        entry.airport.includes(normalizedAirportSearch)
      )
      : airportsVisitedRows;

  const totalAirportVisits =
    airportsVisitedRows.reduce(
      (total, entry) => total + entry.visits,
      0
    );

  const mostVisitedAirport =
    airportsVisitedRows.length > 0
      ? airportsVisitedRows[0]
      : null;
  return (
    <div style={{ width: 'calc(100% - 0.75rem)', maxWidth: 'none', margin: '0.35rem auto', background: '#ffffff', borderRadius: 4, padding: '0.45rem', color: '#111111', boxShadow: 'none', boxSizing: 'border-box' }}>
      <style>
        {`.pilot-logbook-cell-input:focus {
            outline: none;
            background: #eef4ff;
            box-shadow: inset 0 -1px 0 #7a9ad9;
          }
          @media print {
            .pilot-logbook-nonprint,
            .pilot-logbook-delete-selected,
            .pilot-logbook-delete-modal-overlay {
              display: none !important;
            }
            .pilot-logbook-selection-cell {
              display: none !important;
              width: 0 !important;
              min-width: 0 !important;
              padding: 0 !important;
              border: none !important;
            }
            .pilot-logbook-cell-input {
              outline: none !important;
              background: transparent !important;
              border: none !important;
              box-shadow: none !important;
            }
          }
        `}
      </style>
      <div
        className="pilot-logbook-nonprint"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.6rem',
          flexWrap: 'wrap',
          marginBottom: '0.45rem',
          paddingBottom: '0.45rem',
          borderBottom: '1px solid #cfcfcf',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.55rem',
          }}
        >
          <div
  style={{
    width: 64,
    flexShrink: 0,
  }}
>
  <WingitLogo size="tiny" />
</div>

          <h2
            style={{
              color: '#111111',
              fontWeight: 700,
              fontSize: '1.15rem',
              letterSpacing: '0.01em',
              margin: 0,
            }}
          >
            Pilot Logbook
          </h2>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            style={{
                height: 38,
                minHeight: 38,
                margin: 0,
                padding: '0 0.8rem',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                background: '#f5f5f5',
                color: '#111111',
                border: '1px solid #777777',
                borderRadius: 8,
                boxShadow: 'none',
                fontFamily: 'inherit',
                fontSize: '0.78rem',
                fontWeight: 600,
                lineHeight: 1,
                cursor: 'pointer',
                verticalAlign: 'middle',
              }}
          >
            Back to Dashboard
          </button>



          <div
            style={{
              position: 'relative',
            }}
          >
            <button
              type="button"
              onClick={() =>
                setShowLogbookTools((previous) => !previous)
              }
              style={{
                height: 38,
                minHeight: 38,
                margin: 0,
                padding: '0 0.8rem',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                background: '#f5f5f5',
                color: '#111111',
                border: '1px solid #777777',
                borderRadius: 8,
                boxShadow: 'none',
                fontFamily: 'inherit',
                fontSize: '0.78rem',
                fontWeight: 600,
                lineHeight: 1,
                cursor: 'pointer',
                verticalAlign: 'middle',
              }}
            >
              Logbook Tools
            </button>

            {showLogbookTools ? (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 0.3rem)',
                  zIndex: 50,
                  width: 220,
                  padding: '0.3rem',
                  background: '#ffffff',
                  border: '1px solid #777777',
                  borderRadius: 6,
                  boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowLogbookStatistics(true);
                    setShowAirportsVisited(false);
                    setShowLogbookSettings(false);
                    setShowLogbookTools(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.55rem',
                    textAlign: 'left',
                    border: 'none',
                    borderRadius: 4,
                    background: '#ffffff',
                    color: '#111111',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                  }}
                >
                  Statistics
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAirportsVisited(true);
                    setShowLogbookStatistics(false);
                    setShowLogbookSettings(false);
                    setShowLogbookTools(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.55rem',
                    textAlign: 'left',
                    border: 'none',
                    borderRadius: 4,
                    background: '#ffffff',
                    color: '#111111',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                  }}
                >
                  Airports Visited
                </button>

                <button
                  type="button"
                  onClick={() => {
                    handleDownloadPdf();
                    setShowLogbookTools(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.55rem',
                    textAlign: 'left',
                    border: 'none',
                    borderRadius: 4,
                    background: '#ffffff',
                    color: '#111111',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                  }}
                >
                  Download PDF
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowLogbookSettings(true);
                    setShowAirportsVisited(false);
                    setShowLogbookTools(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.55rem',
                    textAlign: 'left',
                    border: 'none',
                    borderRadius: 4,
                    background: '#ffffff',
                    color: '#111111',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                  }}
                >
                  {`Settings - ${selectedTimeFormatLabel}`}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {showLogbookSettings ? (
        <div
          style={{
            ...logbookSettingsWrapStyle,
            marginBottom: '0.5rem',
          }}
        >
          <div style={logbookSettingsPanelStyle}>
            <div style={logbookSettingsLabelStyle}>
              Record flight time as:
            </div>

            <label style={logbookSettingsOptionStyle}>
              <input
                type="radio"
                name="pilotLogbookTimeFormat"
                checked={
                  pilotTimeFormat
                  === LOGBOOK_TIME_FORMAT_HOURS_MINUTES
                }
                onChange={() =>
                  handlePilotTimeFormatChange(
                    LOGBOOK_TIME_FORMAT_HOURS_MINUTES
                  )
                }
                disabled={savingPilotTimeFormat}
              />
              Hours and minutes
            </label>

            <label style={logbookSettingsOptionStyle}>
              <input
                type="radio"
                name="pilotLogbookTimeFormat"
                checked={
                  pilotTimeFormat
                  === LOGBOOK_TIME_FORMAT_DECIMAL_HOURS
                }
                onChange={() =>
                  handlePilotTimeFormatChange(
                    LOGBOOK_TIME_FORMAT_DECIMAL_HOURS
                  )
                }
                disabled={savingPilotTimeFormat}
              />
              Decimal hours
            </label>

            {pilotTimeFormatFeedback.text ? (
              <div
                style={{
                  ...logbookSettingsFeedbackStyle,
                  color:
                    pilotTimeFormatFeedback.type === 'error'
                      ? '#a01818'
                      : '#1b5e20',
                }}
              >
                {pilotTimeFormatFeedback.text}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {showAirportsVisited ? (
        <div
          className="pilot-logbook-nonprint pilot-logbook-airports-panel"
          style={{
            position: 'fixed',
            top: 70,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 250,
            width: 'min(900px, calc(100vw - 2rem))',
            maxHeight: 'calc(100vh - 95px)',
            overflowY: 'auto',
            padding: '0.7rem',
            background: '#f8f9fa',
            border: '1px solid #777777',
            borderRadius: 8,
            boxShadow: '0 12px 35px rgba(0,0,0,0.28)',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '0.6rem',
              marginBottom: '0.6rem',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: '0.95rem',
                  fontWeight: 700,
                }}
              >
                Airports Visited
              </div>

              <div
                style={{
                  marginTop: '0.12rem',
                  fontSize: '0.72rem',
                  color: '#555555',
                }}
              >
                Airport visits from the flights currently shown in your logbook.
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowAirportsVisited(false)}
              style={{
                height: 34,
                padding: '0 0.7rem',
                background: '#ffffff',
                color: '#111111',
                border: '1px solid #777777',
                borderRadius: 6,
                fontFamily: 'inherit',
                fontSize: '0.76rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '0.45rem',
              marginBottom: '0.6rem',
            }}
          >
            <div
              style={{
                padding: '0.55rem',
                background: '#ffffff',
                border: '1px solid #c4c9ce',
                borderRadius: 6,
              }}
            >
              <div style={{ fontSize: '0.7rem', color: '#555555' }}>
                Unique airports
              </div>
              <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>
                {airportsVisitedRows.length}
              </div>
            </div>

            <div
              style={{
                padding: '0.55rem',
                background: '#ffffff',
                border: '1px solid #c4c9ce',
                borderRadius: 6,
              }}
            >
              <div style={{ fontSize: '0.7rem', color: '#555555' }}>
                Total visits
              </div>
              <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>
                {totalAirportVisits}
              </div>
            </div>

            <div
              style={{
                padding: '0.55rem',
                background: '#ffffff',
                border: '1px solid #c4c9ce',
                borderRadius: 6,
              }}
            >
              <div style={{ fontSize: '0.7rem', color: '#555555' }}>
                Most visited
              </div>
              <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>
                {mostVisitedAirport
                  ? `${mostVisitedAirport.airport} (${mostVisitedAirport.visits})`
                  : '�'}
              </div>
            </div>
          </div>

          <input
            type="search"
            value={airportSearch}
            onChange={(event) => setAirportSearch(event.target.value)}
            placeholder="Search airports"
            aria-label="Search airports visited"
            style={{
              width: '100%',
              height: 36,
              marginBottom: '0.55rem',
              padding: '0 0.65rem',
              border: '1px solid #92999f',
              borderRadius: 6,
              background: '#ffffff',
              color: '#111111',
              fontFamily: 'inherit',
              fontSize: '0.8rem',
              boxSizing: 'border-box',
            }}
          />

          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                background: '#ffffff',
                fontSize: '0.76rem',
              }}
            >
              <thead>
                <tr>
                  {[
                    'Airport',
                    'Visits',
                    'Departures',
                    'Arrivals',
                    'First visit',
                    'Last visit',
                  ].map((heading) => (
                    <th
                      key={heading}
                      style={{
                        padding: '0.45rem',
                        textAlign: heading === 'Airport' ? 'left' : 'center',
                        border: '1px solid #b8bec4',
                        background: '#e9ecef',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredAirportsVisitedRows.length > 0 ? (
                  filteredAirportsVisitedRows.map((entry) => (
                    <tr key={entry.airport}>
                      <td
                        style={{
                          padding: '0.45rem',
                          border: '1px solid #c7ccd1',
                          fontWeight: 600,
                        }}
                      >
                        {entry.airport}
                      </td>

                      <td style={{ padding: '0.45rem', textAlign: 'center', border: '1px solid #c7ccd1' }}>
                        {entry.visits}
                      </td>

                      <td style={{ padding: '0.45rem', textAlign: 'center', border: '1px solid #c7ccd1' }}>
                        {entry.departures}
                      </td>

                      <td style={{ padding: '0.45rem', textAlign: 'center', border: '1px solid #c7ccd1' }}>
                        {entry.arrivals}
                      </td>

                      <td style={{ padding: '0.45rem', textAlign: 'center', border: '1px solid #c7ccd1' }}>
                        {entry.firstVisit || '�'}
                      </td>

                      <td style={{ padding: '0.45rem', textAlign: 'center', border: '1px solid #c7ccd1' }}>
                        {entry.lastVisit || '�'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        padding: '1rem',
                        textAlign: 'center',
                        color: '#666666',
                        border: '1px solid #c7ccd1',
                      }}
                    >
                      No airports found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      {showLogbookStatistics ? (
        <div
          className="pilot-logbook-nonprint pilot-logbook-statistics-panel"
          style={{
            position: 'fixed',
            top: 70,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 250,
            width: 'min(1100px, calc(100vw - 2rem))',
            maxHeight: 'calc(100vh - 95px)',
            overflowY: 'auto',
            padding: '0.65rem',
            background: '#f8f9fa',
            border: '1px solid #777777',
            borderRadius: 8,
            boxShadow: '0 12px 35px rgba(0,0,0,0.28)',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '0.6rem',
              marginBottom: '0.55rem',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: '0.95rem',
                  fontWeight: 700,
                }}
              >
                Logbook Statistics
              </div>

              <div
                style={{
                  marginTop: '0.12rem',
                  fontSize: '0.72rem',
                  color: '#555555',
                }}
              >
                Totals include saved starting hours. Charts follow the current filters.
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowLogbookStatistics(false)}
              style={{
                height: 34,
                padding: '0 0.7rem',
                background: '#ffffff',
                color: '#111111',
                border: '1px solid #777777',
                borderRadius: 6,
                fontFamily: 'inherit',
                fontSize: '0.76rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>

          <LogbookStatisticsCharts
            summaryCards={logbookStatisticsSummaryCards}
            monthlyData={logbookStatisticsMonthlyData}
            aircraftData={logbookStatisticsAircraftData}
            airportData={
              airportsVisitedRows
                .slice(0, 10)
                .map((entry) => ({
                  label: entry.airport,
                  visits: entry.visits,
                }))
            }
            capacityData={logbookStatisticsCapacityData}
            timeFormat={pilotTimeFormat}
          />
        </div>
      ) : null}
      {loading ? <div>Loading pilot logbook...</div> : null}
      {error ? (
        <div
          style={{
            color: '#a01818',
            marginBottom: '0.8rem',
          }}
        >
          {error}
        </div>
      ) : null}

      {!loading && !error && (
        <>
          <div
            className="pilot-logbook-nonprint"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              flexWrap: 'wrap',
              marginBottom: '0.5rem',
              padding: '0.42rem',
              background: '#f5f7f9',
              border: '1px solid #b8bec4',
              borderRadius: 6,
            }}
          >
            <input
              type="search"
              value={logbookSearch}
              onChange={(event) =>
                setLogbookSearch(event.target.value)
              }
              placeholder="Search flights"
              aria-label="Search logbook flights"
              style={{
                width: 240,
                minWidth: 170,
                height: 34,
                padding: '0 0.5rem',
                border: '1px solid #858c92',
                borderRadius: 5,
                background: '#ffffff',
                fontSize: '0.78rem',
                boxSizing: 'border-box',
              }}
            />

            <div
              style={{
                position: 'relative',
              }}
            >
              <button
                type="button"
                onClick={() =>
                  setShowLogbookFilters(
                    (previous) => !previous
                  )
                }
                style={{
                  height: 34,
                  padding: '0 0.75rem',
                  background: '#ffffff',
                  color: '#111111',
                  border: '1px solid #777777',
                  borderRadius: 5,
                  fontFamily: 'inherit',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {`Filter ${showLogbookFilters ? '?' : '?'}`}
              </button>

              {showLogbookFilters ? (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 'calc(100% + 0.3rem)',
                    zIndex: 70,
                    width: 245,
                    padding: '0.6rem',
                    background: '#ffffff',
                    border: '1px solid #858c92',
                    borderRadius: 6,
                    boxShadow:
                      '0 7px 20px rgba(0,0,0,0.18)',
                  }}
                >
                  <label
                    style={{
                      display: 'grid',
                      gap: '0.2rem',
                      marginBottom: '0.5rem',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                    }}
                  >
                    Date range
                    <select
                      value={logbookPeriodFilter}
                      onChange={(event) =>
                        setLogbookPeriodFilter(
                          event.target.value
                        )
                      }
                      style={{
                        width: '100%',
                        height: 34,
                        padding: '0 0.4rem',
                        border: '1px solid #858c92',
                        borderRadius: 5,
                        background: '#ffffff',
                        fontSize: '0.78rem',
                      }}
                    >
                      <option value="all">All dates</option>
                      <option value="this-year">
                        This year
                      </option>
                      <option value="last-90-days">
                        Last 90 days
                      </option>
                      <option value="previous-year">
                        Previous year
                      </option>
                    </select>
                  </label>

                  <label
                    style={{
                      display: 'grid',
                      gap: '0.2rem',
                      marginBottom: '0.5rem',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                    }}
                  >
                    Flying capacity
                    <select
                      value={logbookCapacityFilter}
                      onChange={(event) =>
                        setLogbookCapacityFilter(
                          event.target.value
                        )
                      }
                      style={{
                        width: '100%',
                        height: 34,
                        padding: '0 0.4rem',
                        border: '1px solid #858c92',
                        borderRadius: 5,
                        background: '#ffffff',
                        fontSize: '0.78rem',
                      }}
                    >
                      <option value="all">
                        All flying
                      </option>
                      <option value="pic">
                        Pilot in command
                      </option>
                      <option value="dual">Dual</option>
                      <option value="night">Night</option>
                      <option value="instrument">
                        Instrument
                      </option>
                    </select>
                  </label>

                  <label
                    style={{
                      display: 'grid',
                      gap: '0.2rem',
                      marginBottom: '0.6rem',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                    }}
                  >
                    Flight order
                    <select
                      value={logbookSortOrder}
                      onChange={(event) =>
                        setLogbookSortOrder(
                          event.target.value
                        )
                      }
                      style={{
                        width: '100%',
                        height: 34,
                        padding: '0 0.4rem',
                        border: '1px solid #858c92',
                        borderRadius: 5,
                        background: '#ffffff',
                        fontSize: '0.78rem',
                      }}
                    >
                      <option value="newest">
                        Newest first
                      </option>
                      <option value="oldest">
                        Oldest first
                      </option>
                    </select>
                  </label>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '0.4rem',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setLogbookPeriodFilter('all');
                        setLogbookCapacityFilter('all');
                        setLogbookSortOrder('newest');
                      }}
                      style={{
                        height: 32,
                        padding: '0 0.6rem',
                        background: '#ffffff',
                        color: '#111111',
                        border: '1px solid #777777',
                        borderRadius: 5,
                        fontSize: '0.74rem',
                        cursor: 'pointer',
                      }}
                    >
                      Reset filters
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setShowLogbookFilters(false)
                      }
                      style={{
                        height: 32,
                        padding: '0 0.7rem',
                        background: '#eceff3',
                        color: '#111111',
                        border: '1px solid #777777',
                        borderRadius: 5,
                        fontSize: '0.74rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <span
              style={{
                fontSize: '0.74rem',
                color: '#555555',
                whiteSpace: 'nowrap',
              }}
            >
              {`${displayRows.filter(
                (row) => !row.isManualDraft
              ).length} of ${rows.filter(
                (row) => !row.isManualDraft
              ).length} flights`}
            </span>

            {(
              logbookSearch
              || logbookPeriodFilter !== 'all'
              || logbookCapacityFilter !== 'all'
              || logbookSortOrder !== 'newest'
            ) ? (
              <button
                type="button"
                onClick={() => {
                  setLogbookSearch('');
                  setLogbookPeriodFilter('all');
                  setLogbookCapacityFilter('all');
                  setLogbookSortOrder('newest');
                }}
                style={{
                  height: 34,
                  padding: '0 0.65rem',
                  background: '#ffffff',
                  color: '#111111',
                  border: '1px solid #777777',
                  borderRadius: 5,
                  fontSize: '0.76rem',
                  cursor: 'pointer',
                }}
              >
                Clear
              </button>
            ) : null}
          </div>
<div style={compactLogbookActionRowStyle}>
            <button
              type="button"
              onClick={handleSaveLogbookChanges}
              disabled={savingAllChanges || !hasUnsavedLogbookChanges || hasOverAllocatedRows}
              style={{
                ...buttonStyle,
                background: '#f5f5f5',
                color: '#111111',
                border: '1px solid #222222',
                boxShadow: 'none',
                padding: '0.35rem 0.8rem',
                fontSize: '0.84rem',
                opacity: (savingAllChanges || !hasUnsavedLogbookChanges || hasOverAllocatedRows) ? 0.7 : 1,
                cursor: (savingAllChanges || !hasUnsavedLogbookChanges || hasOverAllocatedRows) ? 'not-allowed' : 'pointer',
              }}
            >
              {savingAllChanges ? 'Saving...' : 'Save Logbook Changes'}
            </button>
            <span
              className="pilot-logbook-nonprint"
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                color:
                  bulkSaveFeedback.type === 'error'
                    ? '#a01818'
                    : savingAllChanges
                      ? '#555555'
                      : hasUnsavedLogbookChanges
                        ? '#8a5a00'
                        : '#1b5e20',
              }}
            >
              {bulkSaveFeedback.type === 'error'
                ? 'Save failed'
                : savingAllChanges
                  ? 'Saving...'
                  : hasUnsavedLogbookChanges
                    ? `${unsavedChangeCount} unsaved ${
                      unsavedChangeCount === 1
                        ? 'change'
                        : 'changes'
                    }`
                    : 'All changes saved'}
            </span>

            <button
              type="button"
              onClick={handleAddManualFlight}
              disabled={hasManualDraft || savingAllChanges}
              style={{
                ...buttonStyle,
                background: '#f5f5f5',
                color: '#111111',
                border: '1px solid #222222',
                boxShadow: 'none',
                padding: '0.35rem 0.8rem',
                fontSize: '0.84rem',
                opacity: (hasManualDraft || savingAllChanges) ? 0.7 : 1,
                cursor: (hasManualDraft || savingAllChanges) ? 'not-allowed' : 'pointer',
              }}
            >
              Add Flight
            </button>

            {hasSelectedManualRows ? (
            <button
              type="button"
              className="pilot-logbook-nonprint pilot-logbook-delete-selected"
              onClick={handleRequestDeleteSelected}
              disabled={!hasSelectedManualRows || savingAllChanges || deletingSelectedManualRows}
              style={{
                ...buttonStyle,
                background: '#fff5f5',
                color: '#8b1a1a',
                border: '1px solid #9a2a2a',
                boxShadow: 'none',
                padding: '0.35rem 0.72rem',
                fontSize: '0.82rem',
                opacity: (!hasSelectedManualRows || savingAllChanges || deletingSelectedManualRows) ? 0.7 : 1,
                cursor: (!hasSelectedManualRows || savingAllChanges || deletingSelectedManualRows) ? 'not-allowed' : 'pointer',
              }}
            >
              {deletingSelectedManualRows ? 'Deleting...' : 'Delete Selected'}
            </button>
            ) : null}

            {hasManualDraft ? (
              <>
                <button
                  type="button"
                  onClick={handleCancelManualEntry}
                  disabled={savingAllChanges}
                  style={{
                    ...buttonStyle,
                    background: '#eceff3',
                    color: '#111111',
                    border: '1px solid #222222',
                    boxShadow: 'none',
                    padding: '0.35rem 0.8rem',
                    fontSize: '0.84rem',
                    opacity: savingAllChanges ? 0.7 : 1,
                    cursor: savingAllChanges ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel Manual Entry
                </button>
              </>
            ) : null}

            {selectedBookingId && !selectedBookingDidNotFly ? (
              <>
              {selectedFlightFeedback.text ? (
                <div
                  style={{
                    ...selectedFlightFeedbackStyle,
                    color: selectedFlightFeedback.type === 'error'
                      ? '#a01818'
                      : (selectedFlightFeedback.type === 'warning' ? '#8a5b00' : '#1b5e20'),
                  }}
                >
                  {selectedFlightFeedback.text}
                </div>
              ) : null}
              {selectedFlightDraft && !selectedFlightHasExistingEntry && (Number.isFinite(Number(selectedFlightDraft.autoDurationMinutes)) || Number.isFinite(Number(selectedFlightDraft.totalTime || selectedFlightDraft.flightTime))) ? (
                <div style={{ ...selectedFlightFeedbackStyle, color: '#1f1f1f' }}>
                  Flight duration pre-filled in {
                    selectedFlightDraft.isTrainingFlight
                      ? 'Dual (1)'
                      : 'P in C (2)'
                  }: {
                    Number.isFinite(Number(selectedFlightDraft.autoDurationMinutes))
                      ? formatDurationMinutesForDisplay(Number(selectedFlightDraft.autoDurationMinutes), pilotTimeFormat)
                      : formatHourNumberForDisplay(Number(selectedFlightDraft.totalTime || selectedFlightDraft.flightTime), pilotTimeFormat)
                  }
                </div>
              ) : null}
              {selectedBookingResolution === 'missing' ? (
                <div style={{ ...selectedFlightFeedbackStyle, color: '#6a3a00' }}>
                  Booking not found for this logbook request.
                </div>
              ) : null}
              {selectedBookingResolution === 'ineligible' ? (
                <div style={{ ...selectedFlightFeedbackStyle, color: '#6a3a00' }}>
                  Pilot Logbook will become available after Post Flight is completed.
                </div>
              ) : null}
              {selectedFlightHasExistingEntry ? (
                <div style={{ ...selectedFlightFeedbackStyle, color: '#1f1f1f' }}>
                  Existing logbook entry already saved for this booking.
                </div>
              ) : null}
              {selectedBookingCompletionMismatch ? (
                <div
                  style={{
                    ...selectedFlightFeedbackStyle,
                    color: '#8a5b00',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.6rem',
                    flexWrap: 'wrap',
                  }}
                >
                  <span>Logbook entry is saved, but this booking was not marked complete.</span>
                  <button
                    type="button"
                    onClick={handleRetryBookingCompletion}
                    disabled={retryingBookingCompletion}
                    style={{
                      ...buttonStyle,
                      padding: '0.3rem 0.7rem',
                      fontSize: '0.78rem',
                      opacity: retryingBookingCompletion ? 0.7 : 1,
                      cursor: retryingBookingCompletion ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {retryingBookingCompletion ? 'Marking complete...' : 'Mark booking complete'}
                  </button>
                </div>
              ) : null}
              </>
            ) : null}

          </div>

          <div
            className="pilot-logbook-nonprint"
            style={{
              ...selectedFlightFeedbackStyle,
              marginTop: '-0.15rem',
              marginBottom: '0.45rem',
              display: topFeedbackText ? 'block' : 'none',
        minHeight: 0,
            }}
          >
            {topFeedbackText ? (
              <div
                style={{
                  color: topFeedbackType === 'error' ? '#a01818' : '#1b5e20',
                  border: `1px solid ${topFeedbackType === 'error' ? '#e2b1b1' : '#b7d7bf'}`,
                  background: topFeedbackType === 'error' ? '#fff8f8' : '#f5fff8',
                  padding: '0.3rem 0.55rem',
                  fontSize: '0.78rem',
                  borderRadius: 4,
                }}
              >
                {topFeedbackText}
              </div>
            ) : null}
          </div>

          {isPhone ? (
            <MobilePilotLogbook
              rows={displayRows.map((row) => ({
                ...row,
                seDayDual: resolveSeDayDualValueForDisplay(
                  row,
                  pilotTimeFormat
                ),
                seDayPic: resolveSeDayPicValueForDisplay(
                  row,
                  pilotTimeFormat
                ),
              }))}
              aircraftMap={aircraftMap}
              totals={liveCarriedForwardTotals}
              formatTotal={formatHoursTotal}
              normalizeRow={normalizeLogbookRowForDisplay}
              updateCell={updateLogbookCell}
              commitDate={commitDateLogbookCell}
              updateUtc={updateUtcLogbookCell}
              commitUtc={commitUtcLogbookCell}
              commitHours={commitHourLogbookCell}
              rowFieldErrors={rowFieldErrors}
              utcFieldErrors={utcFieldErrors}
              getFieldErrorKey={getUtcFieldErrorKey}
              isSelectableManualRow={isSelectableManualRow}
              selectedManualRowIds={selectedManualRowIds}
              toggleManualRowSelection={toggleManualRowSelection}
              savingRowId={savingRowId}
            />
          ) : null}

          <div className="pilot-logbook-desktop-sheet" style={{ overflowX: useMobileTableScroll ? 'auto' : 'hidden' }}>
            <div style={{ minWidth: 2333, width: 2333, zoom: useMobileTableScroll ? 1 : desktopSheetScale }}>
                <div style={{ background: '#f6f6f2', border: '2px solid #2b2b2b', borderRadius: 6 }}>
              <div style={{ minWidth: 2333, width: 2333 }}>
              <div style={{ borderBottom: '1px solid #2b2b2b', background: '#f1f1f1', padding: '0.45rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Arial, Helvetica, sans-serif', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 28 }} />
                    <col style={{ width: 95 }} />
                    <col style={{ width: 75 }} />
                    <col style={{ width: 95 }} />
                    <col style={{ width: 130 }} />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 145 }} />
                    <col style={{ width: 145 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 92 }} />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 180 }} />
                  </colgroup>
                  <tbody>
                    {!initialHoursSaved
                      && !initialHoursLocked ? (
                      <tr className="pilot-logbook-nonprint">
                        <td
                          colSpan={10}
                          style={{
                            border: 'none',
                            padding: 0,
                            background: 'transparent',
                          }}
                        ></td>

                        <td
                          colSpan={8}
                          style={{
                            border: 'none',
                            padding: '0 0 0.4rem',
                            background: 'transparent',
                          }}
                        >
                          <div
                            style={{
                              position: 'relative',
                              width: '100%',
                              border: '2px solid #b42318',
                              borderRadius: 3,
                              background: '#fffafa',
                              padding: '0.5rem 0.65rem',
                              color: '#4a1515',
                              boxSizing: 'border-box',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '0.65rem',
                              flexWrap: 'wrap',
                              textAlign: 'left',
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: '0.8rem',
                                  fontWeight: 800,
                                  marginBottom: 2,
                                }}
                              >
                                Enter your starting totals
                              </div>

                              <div
                                style={{
                                  fontSize: '0.7rem',
                                  lineHeight: 1.35,
                                }}
                              >
                                Copy the totals carried forward
                                from your paper logbook into the
                                highlighted boxes below.
                              </div>
                            </div>

                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.4rem',
                                flexShrink: 0,
                              }}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  handleSaveInitialHours()
                                }
                                disabled={savingInitialHours}
                                style={{
                                  ...buttonStyle,
                                  padding: '0.32rem 0.6rem',
                                  fontSize: '0.7rem',
                                  background: '#b42318',
                                  color: '#ffffff',
                                  boxShadow: 'none',
                                  opacity:
                                    savingInitialHours
                                      ? 0.65
                                      : 1,
                                }}
                              >
                                {savingInitialHours
                                  ? 'Saving...'
                                  : 'Save'}
                              </button>

                              <button
                                type="button"
                                onClick={
                                  handleConfirmNoPreviousHours
                                }
                                disabled={savingInitialHours}
                                style={{
                                  ...buttonStyle,
                                  padding: '0.32rem 0.6rem',
                                  fontSize: '0.7rem',
                                  background: '#ffffff',
                                  color: '#641414',
                                  border: '1px solid #b42318',
                                  boxShadow: 'none',
                                  opacity:
                                    savingInitialHours
                                      ? 0.65
                                      : 1,
                                }}
                              >
                                No previous hours
                              </button>
                            </div>

                            {initialHoursFeedback.text ? (
                              <div
                                style={{
                                  width: '100%',
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                }}
                              >
                                {initialHoursFeedback.text}
                              </div>
                            ) : null}

                            <div
                              style={{
                                position: 'absolute',
                                left: '6.25%',
                                bottom: -16,
                                width: 2,
                                height: 16,
                                background: '#b42318',
                                transform: 'translateX(-1px)',
                              }}
                            />
                          </div>
                        </td>

                        <td
                          colSpan={7}
                          style={{
                            border: 'none',
                            padding: 0,
                            background: 'transparent',
                          }}
                        ></td>
                      </tr>
                    ) : null}
                    <tr>
                      <td
                        style={tdSelectionSpacerStyle}
                        className="pilot-logbook-nonprint pilot-logbook-selection-cell"
                      ></td>

                      <td
                        style={tdBroughtForwardLabelStyle}
                        colSpan={9}
                      >
                        TOTALS BROUGHT FORWARD
                      </td>

                      {renderBroughtForwardCell(
                        'seDayDual',
                        'Initial Hours Dual 1',
                        true
                      )}
                      {renderBroughtForwardCell(
                        'seDayPic',
                        'Initial Hours P in C 2'
                      )}
                      {renderBroughtForwardCell(
                        'seNightDual',
                        'Initial Hours Dual 3'
                      )}
                      {renderBroughtForwardCell(
                        'seNightPic',
                        'Initial Hours P in C 4'
                      )}
                      {renderBroughtForwardCell(
                        'meDayDual',
                        'Initial Hours Dual 5'
                      )}
                      {renderBroughtForwardCell(
                        'meDayPic',
                        'Initial Hours P in C 6'
                      )}
                      {renderBroughtForwardCell(
                        'meDayCopilot',
                        'Initial Hours Co-pilot 7'
                      )}
                      {renderBroughtForwardCell(
                        'meDayPiUs',
                        'Initial Hours PI US 8'
                      )}
                      {renderBroughtForwardCell(
                        'meNightDual',
                        'Initial Hours Dual 9'
                      )}
                      {renderBroughtForwardCell(
                        'meNightPic',
                        'Initial Hours P in C 10'
                      )}
                      {renderBroughtForwardCell(
                        'meNightCopilot',
                        'Initial Hours Co-pilot 11'
                      )}
                      {renderBroughtForwardCell(
                        'meNightPiUs',
                        'Initial Hours PI US 12'
                      )}
                      {renderBroughtForwardCell(
                        'instrumentFlight',
                        'Initial Hours Instrument 13'
                      )}
                      {renderBroughtForwardCell(
                        'otherFlying',
                        'Initial Hours Other 14'
                      )}

                      <td style={tdStyle}></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Arial, Helvetica, sans-serif', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: 28 }} />
                  <col style={{ width: 95 }} />
                  <col style={{ width: 75 }} />
                  <col style={{ width: 95 }} />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 145 }} />
                  <col style={{ width: 145 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 92 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 180 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={thSelectionSpacerStyle} className="pilot-logbook-nonprint pilot-logbook-selection-cell"></th>
                    <th style={thLeadStyle} rowSpan={3}>Date</th>
                    <th style={thLeadStyle} rowSpan={3}>Type</th>
                    <th style={thLeadStyle} rowSpan={3}>Reg</th>
                    <th style={thLeadStyle} rowSpan={3}>Pilot</th>
                    <th style={thLeadStyle} rowSpan={3}>student</th>
                    <th style={thLeadStyle} rowSpan={3}>To</th>
                    <th style={thLeadStyle} rowSpan={3}>From</th>
                    <th style={thLeadStyle} rowSpan={3}><span style={{ display: 'block' }}>Dep</span><span style={{ display: 'block', fontSize: '0.66rem' }}>(UTC)</span></th>
                    <th style={thLeadStyle} rowSpan={3}><span style={{ display: 'block' }}>Arr</span><span style={{ display: 'block', fontSize: '0.66rem' }}>(UTC)</span></th>
                    <th style={thGroupStyle} colSpan={4}>Single-engined Aircraft</th>
                    <th style={thGroupStyle} colSpan={8}>Multi-engined Aircraft</th>
                    <th style={thGroupStyle} rowSpan={3}>Instrument Flight (13)</th>
                    <th style={thGroupStyle} rowSpan={3}>F/NAV F/ENG RTO Other Flying (14)</th>
                    <th style={thGroupStyle} rowSpan={3}>Remarks (15)</th>
                  </tr>
                  <tr>
                    <th style={thSelectionSpacerStyle} className="pilot-logbook-nonprint pilot-logbook-selection-cell"></th>
                    <th style={thSubGroupStyle} colSpan={2}>Day</th>
                    <th style={thSubGroupStyle} colSpan={2}>Night</th>
                    <th style={thSubGroupStyle} colSpan={4}>Day</th>
                    <th style={thSubGroupStyle} colSpan={4}>Night</th>
                  </tr>
                  <tr>
                    <th style={thSelectionSpacerStyle} className="pilot-logbook-nonprint pilot-logbook-selection-cell"></th>
                    <th style={thLeafStyle}>Dual (1)</th>
                    <th style={thLeafStyle}>P in C (2)</th>
                    <th style={thLeafStyle}>Dual (3)</th>
                    <th style={thLeafStyle}>P in C (4)</th>
                    <th style={thLeafStyle}>Dual (5)</th>
                    <th style={thLeafStyle}>P in C (6)</th>
                    <th style={thLeafStyle}>Co-pilot (7)</th>
                    <th style={thLeafStyle}>PI (US) (8)</th>
                    <th style={thLeafStyle}>Dual (9)</th>
                    <th style={thLeafStyle}>P in C (10)</th>
                    <th style={thLeafStyle}>Co-pilot (11)</th>
                    <th style={thLeafStyle}>PI (US) (12)</th>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((row) => {
                    const aircraft = aircraftMap[row.aircraftId];
                    const normalizedRow = normalizeLogbookRowForDisplay(row, aircraft);
                    const hourInputMode = pilotTimeFormat === LOGBOOK_TIME_FORMAT_HOURS_MINUTES ? 'text' : 'decimal';
                    const seDayPicDisplayValue = resolveSeDayPicValueForDisplay(row, pilotTimeFormat);
                    const remarksError = rowFieldErrors[getUtcFieldErrorKey(row.bookingId, 'notes')];
                    const dateError = rowFieldErrors[getUtcFieldErrorKey(row.bookingId, 'date')];
                    return (
                      <tr key={row.bookingId}>
                        <td style={tdSelectionSpacerStyle} className="pilot-logbook-nonprint pilot-logbook-selection-cell">
                          {isSelectableManualRow(row) ? (
                            <input
                              type="checkbox"
                              checked={Boolean(selectedManualRowIds[String(row.bookingId)])}
                              onChange={(e) => toggleManualRowSelection(String(row.bookingId), e.target.checked)}
                              aria-label={`Select manual entry ${String(row.bookingId)}`}
                            />
                          ) : null}
                        </td>
                        <td style={tdLeadStyle}>
                          <input className="pilot-logbook-cell-input" value={row.date || ''} onChange={(e) => updateLogbookCell(row.bookingId, 'date', e.target.value)} onBlur={() => commitDateLogbookCell(row.bookingId)} style={spreadsheetInputStyle} />
                          {dateError ? <div style={utcFieldErrorStyle}>{dateError}</div> : null}
                        </td>
                        <td style={tdLeadStyle}>
                          <input className="pilot-logbook-cell-input" value={normalizedRow.aircraftType} onChange={(e) => updateLogbookCell(row.bookingId, 'aircraftType', e.target.value)} style={spreadsheetInputStyle} />
                        </td>
                        <td style={tdLeadStyle}>
                          <input className="pilot-logbook-cell-input" value={normalizedRow.aircraftRegistration} onChange={(e) => updateLogbookCell(row.bookingId, 'aircraftRegistration', e.target.value)} style={spreadsheetInputStyle} />
                        </td>

                        <td style={tdLeadStyle}>
                          <input className="pilot-logbook-cell-input" value={normalizedRow.pilot} onChange={(e) => updateLogbookCell(row.bookingId, 'pilot', e.target.value)} style={spreadsheetInputStyle} />
                        </td>
                        <td style={tdLeadStyle}>
                          <input className="pilot-logbook-cell-input" value={normalizedRow.student} onChange={(e) => updateLogbookCell(row.bookingId, 'student', e.target.value)} style={spreadsheetInputStyle} />
                        </td>
                        <td style={tdLeadStyle}>
                          <input className="pilot-logbook-cell-input" value={normalizedRow.to} onChange={(e) => updateLogbookCell(row.bookingId, 'to', e.target.value)} style={spreadsheetInputStyle} />
                        </td>
                        <td style={tdLeadStyle}>
                          <input className="pilot-logbook-cell-input" value={normalizedRow.from} onChange={(e) => updateLogbookCell(row.bookingId, 'from', e.target.value)} style={spreadsheetInputStyle} />
                        </td>
                        <td style={tdLeadStyle}>
                          <input
                            className="pilot-logbook-cell-input"
                            type="text"
                            inputMode="numeric"
                            value={normalizedRow.depUtc}
                            onChange={(e) => updateUtcLogbookCell(row.bookingId, 'depUtc', e.target.value)}
                            onBlur={() => commitUtcLogbookCell(row.bookingId, 'depUtc')}
                            style={spreadsheetInputStyle}
                          />
                          {utcFieldErrors[getUtcFieldErrorKey(row.bookingId, 'depUtc')] ? (
                            <div style={utcFieldErrorStyle}>{utcFieldErrors[getUtcFieldErrorKey(row.bookingId, 'depUtc')]}</div>
                          ) : null}
                          {rowFieldErrors[getUtcFieldErrorKey(row.bookingId, 'depUtc')] ? (
                            <div style={utcFieldErrorStyle}>{rowFieldErrors[getUtcFieldErrorKey(row.bookingId, 'depUtc')]}</div>
                          ) : null}
                        </td>
                        <td style={tdLeadStyle}>
                          <input
                            className="pilot-logbook-cell-input"
                            type="text"
                            inputMode="numeric"
                            value={normalizedRow.arrUtc}
                            onChange={(e) => updateUtcLogbookCell(row.bookingId, 'arrUtc', e.target.value)}
                            onBlur={() => commitUtcLogbookCell(row.bookingId, 'arrUtc')}
                            style={spreadsheetInputStyle}
                          />
                          {utcFieldErrors[getUtcFieldErrorKey(row.bookingId, 'arrUtc')] ? (
                            <div style={utcFieldErrorStyle}>{utcFieldErrors[getUtcFieldErrorKey(row.bookingId, 'arrUtc')]}</div>
                          ) : null}
                          {rowFieldErrors[getUtcFieldErrorKey(row.bookingId, 'arrUtc')] ? (
                            <div style={utcFieldErrorStyle}>{rowFieldErrors[getUtcFieldErrorKey(row.bookingId, 'arrUtc')]}</div>
                          ) : null}
                        </td>

                        <td style={tdStyle}><input className="pilot-logbook-cell-input" inputMode={hourInputMode} value={resolveSeDayDualValueForDisplay(row, pilotTimeFormat)} onFocus={(e) => handleEligibleAllocationCellFocus(row.bookingId, 'seDayDual', e)} onChange={(e) => updateLogbookCell(row.bookingId, 'seDayDual', e.target.value)} onBlur={() => commitHourLogbookCell(row.bookingId, 'seDayDual')} style={spreadsheetInputStyle} /></td>
                        <td style={tdStyle}><input className="pilot-logbook-cell-input" inputMode={hourInputMode} value={seDayPicDisplayValue} onFocus={(e) => handleEligibleAllocationCellFocus(row.bookingId, 'seDayPic', e)} onChange={(e) => updateLogbookCell(row.bookingId, 'seDayPic', e.target.value)} onBlur={() => commitHourLogbookCell(row.bookingId, 'seDayPic')} style={spreadsheetInputStyle} /></td>
                        <td style={tdStyle}><input className="pilot-logbook-cell-input" inputMode={hourInputMode} value={row.seNightDual || ''} onFocus={(e) => handleEligibleAllocationCellFocus(row.bookingId, 'seNightDual', e)} onChange={(e) => updateLogbookCell(row.bookingId, 'seNightDual', e.target.value)} onBlur={() => commitHourLogbookCell(row.bookingId, 'seNightDual')} style={spreadsheetInputStyle} /></td>
                        <td style={tdStyle}><input className="pilot-logbook-cell-input" inputMode={hourInputMode} value={row.seNightPic || ''} onFocus={(e) => handleEligibleAllocationCellFocus(row.bookingId, 'seNightPic', e)} onChange={(e) => updateLogbookCell(row.bookingId, 'seNightPic', e.target.value)} onBlur={() => commitHourLogbookCell(row.bookingId, 'seNightPic')} style={spreadsheetInputStyle} /></td>

                        <td style={tdStyle}><input className="pilot-logbook-cell-input" inputMode={hourInputMode} value={row.meDayDual || ''} onFocus={(e) => handleEligibleAllocationCellFocus(row.bookingId, 'meDayDual', e)} onChange={(e) => updateLogbookCell(row.bookingId, 'meDayDual', e.target.value)} onBlur={() => commitHourLogbookCell(row.bookingId, 'meDayDual')} style={spreadsheetInputStyle} /></td>
                        <td style={tdStyle}><input className="pilot-logbook-cell-input" inputMode={hourInputMode} value={row.meDayPic || ''} onFocus={(e) => handleEligibleAllocationCellFocus(row.bookingId, 'meDayPic', e)} onChange={(e) => updateLogbookCell(row.bookingId, 'meDayPic', e.target.value)} onBlur={() => commitHourLogbookCell(row.bookingId, 'meDayPic')} style={spreadsheetInputStyle} /></td>
                        <td style={tdStyle}><input className="pilot-logbook-cell-input" inputMode={hourInputMode} value={row.meDayCopilot || ''} onFocus={(e) => handleEligibleAllocationCellFocus(row.bookingId, 'meDayCopilot', e)} onChange={(e) => updateLogbookCell(row.bookingId, 'meDayCopilot', e.target.value)} onBlur={() => commitHourLogbookCell(row.bookingId, 'meDayCopilot')} style={spreadsheetInputStyle} /></td>
                        <td style={tdStyle}><input className="pilot-logbook-cell-input" inputMode={hourInputMode} value={row.meDayPiUs || ''} onFocus={(e) => handleEligibleAllocationCellFocus(row.bookingId, 'meDayPiUs', e)} onChange={(e) => updateLogbookCell(row.bookingId, 'meDayPiUs', e.target.value)} onBlur={() => commitHourLogbookCell(row.bookingId, 'meDayPiUs')} style={spreadsheetInputStyle} /></td>

                        <td style={tdStyle}><input className="pilot-logbook-cell-input" inputMode={hourInputMode} value={row.meNightDual || ''} onFocus={(e) => handleEligibleAllocationCellFocus(row.bookingId, 'meNightDual', e)} onChange={(e) => updateLogbookCell(row.bookingId, 'meNightDual', e.target.value)} onBlur={() => commitHourLogbookCell(row.bookingId, 'meNightDual')} style={spreadsheetInputStyle} /></td>
                        <td style={tdStyle}><input className="pilot-logbook-cell-input" inputMode={hourInputMode} value={row.meNightPic || ''} onFocus={(e) => handleEligibleAllocationCellFocus(row.bookingId, 'meNightPic', e)} onChange={(e) => updateLogbookCell(row.bookingId, 'meNightPic', e.target.value)} onBlur={() => commitHourLogbookCell(row.bookingId, 'meNightPic')} style={spreadsheetInputStyle} /></td>
                        <td style={tdStyle}><input className="pilot-logbook-cell-input" inputMode={hourInputMode} value={row.meNightCopilot || ''} onFocus={(e) => handleEligibleAllocationCellFocus(row.bookingId, 'meNightCopilot', e)} onChange={(e) => updateLogbookCell(row.bookingId, 'meNightCopilot', e.target.value)} onBlur={() => commitHourLogbookCell(row.bookingId, 'meNightCopilot')} style={spreadsheetInputStyle} /></td>
                        <td style={tdStyle}><input className="pilot-logbook-cell-input" inputMode={hourInputMode} value={row.meNightPiUs || ''} onFocus={(e) => handleEligibleAllocationCellFocus(row.bookingId, 'meNightPiUs', e)} onChange={(e) => updateLogbookCell(row.bookingId, 'meNightPiUs', e.target.value)} onBlur={() => commitHourLogbookCell(row.bookingId, 'meNightPiUs')} style={spreadsheetInputStyle} /></td>

                        <td style={tdStyle}><input className="pilot-logbook-cell-input" inputMode={hourInputMode} value={row.instrumentFlight || ''} onChange={(e) => updateLogbookCell(row.bookingId, 'instrumentFlight', e.target.value)} onBlur={() => commitHourLogbookCell(row.bookingId, 'instrumentFlight')} style={spreadsheetInputStyle} /></td>
                        <td style={tdStyle}><input className="pilot-logbook-cell-input" inputMode={hourInputMode} value={row.otherFlying || ''} onChange={(e) => updateLogbookCell(row.bookingId, 'otherFlying', e.target.value)} onBlur={() => commitHourLogbookCell(row.bookingId, 'otherFlying')} style={spreadsheetInputStyle} /></td>
                        <td style={tdStyle}>
                          <input className="pilot-logbook-cell-input" value={normalizedRow.notes} onChange={(e) => updateLogbookCell(row.bookingId, 'notes', e.target.value)} style={spreadsheetInputStyle} />
                          {remarksError ? <div style={utcFieldErrorStyle}>{remarksError}</div> : null}
                          {savingRowId === String(row.bookingId) ? <div style={{ fontSize: '0.62rem', color: '#555555' }}>Saving...</div> : null}
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td style={tdSelectionSpacerStyle} className="pilot-logbook-nonprint pilot-logbook-selection-cell"></td>
                    <td style={tdBroughtForwardLabelStyle} colSpan={9}>TOTALS CARRIED FORWARD</td>
                    <td style={tdStyle}>{formatHoursTotal(liveCarriedForwardTotals.seDayDual)}</td>
                    <td style={tdStyle}>{formatHoursTotal(liveCarriedForwardTotals.seDayPic)}</td>
                    <td style={tdStyle}>{formatHoursTotal(liveCarriedForwardTotals.seNightDual)}</td>
                    <td style={tdStyle}>{formatHoursTotal(liveCarriedForwardTotals.seNightPic)}</td>
                    <td style={tdStyle}>{formatHoursTotal(liveCarriedForwardTotals.meDayDual)}</td>
                    <td style={tdStyle}>{formatHoursTotal(liveCarriedForwardTotals.meDayPic)}</td>
                    <td style={tdStyle}>{formatHoursTotal(liveCarriedForwardTotals.meDayCopilot)}</td>
                    <td style={tdStyle}>{formatHoursTotal(liveCarriedForwardTotals.meDayPiUs)}</td>
                    <td style={tdStyle}>{formatHoursTotal(liveCarriedForwardTotals.meNightDual)}</td>
                    <td style={tdStyle}>{formatHoursTotal(liveCarriedForwardTotals.meNightPic)}</td>
                    <td style={tdStyle}>{formatHoursTotal(liveCarriedForwardTotals.meNightCopilot)}</td>
                    <td style={tdStyle}>{formatHoursTotal(liveCarriedForwardTotals.meNightPiUs)}</td>
                    <td style={tdStyle}>{formatHoursTotal(liveCarriedForwardTotals.instrumentFlight)}</td>
                    <td style={tdStyle}>{formatHoursTotal(liveCarriedForwardTotals.otherFlying)}</td>
                    <td style={tdStyle}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          </div>
          </div>
        </>
      )}

      {showDeleteSelectedConfirm ? (
        <div className="pilot-logbook-nonprint pilot-logbook-delete-modal-overlay" style={deleteModalOverlayStyle}>
          <div style={deleteModalCardStyle}>
            <div style={deleteModalHeadingStyle}>Delete selected logbook entries?</div>
            <div style={deleteModalMessageStyle}>
              This permanently removes the selected manual logbook entries and updates the totals.
            </div>
            <div style={deleteModalActionsStyle}>
              <button
                type="button"
                onClick={handleConfirmDeleteSelected}
                disabled={deletingSelectedManualRows}
                style={deleteModalDeleteButtonStyle}
              >
                {deletingSelectedManualRows ? 'Deleting...' : 'Delete Entries'}
              </button>
              <button
                type="button"
                onClick={handleCancelDeleteSelected}
                disabled={deletingSelectedManualRows}
                style={deleteModalCancelButtonStyle}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const thGroupStyle = {
  borderBottom: '1px solid #2b2b2b',
  borderRight: '1px solid #2b2b2b',
  background: '#ffffff',
  color: '#1a1a1a',
  textAlign: 'center',
  padding: '0.35rem 0.45rem',
  fontSize: '0.8rem',
  fontWeight: 700,
};

const thLeadStyle = {
  ...thGroupStyle,
  textAlign: 'left',
  paddingLeft: '0.55rem',
};

const thSubGroupStyle = {
  borderBottom: '1px solid #2b2b2b',
  borderRight: '1px solid #2b2b2b',
  background: '#ffffff',
  color: '#202020',
  textAlign: 'center',
  padding: '0.3rem 0.4rem',
  fontSize: '0.76rem',
  fontWeight: 700,
};

const thLeafStyle = {
  borderBottom: '1px solid #2b2b2b',
  borderRight: '1px solid #2b2b2b',
  background: '#ffffff',
  color: '#222222',
  textAlign: 'center',
  padding: '0.28rem 0.35rem',
  fontSize: '0.72rem',
  fontWeight: 600,
};

const tdStyle = {
  borderBottom: '1px solid #6f6f6f',
  borderRight: '1px solid #6f6f6f',
  background: '#ffffff',
  color: '#151515',
  padding: '0.22rem 0.35rem',
  fontSize: '0.75rem',
  lineHeight: 1.2,
  verticalAlign: 'middle',
  textAlign: 'center',
};

const tdLeadStyle = {
  ...tdStyle,
  textAlign: 'left',
  paddingLeft: '0.45rem',
};

const tdSelectionSpacerStyle = {
  width: 28,
  minWidth: 28,
  maxWidth: 28,
  border: 'none',
  background: 'transparent',
  padding: '0.22rem 0.2rem 0.22rem 0',
  textAlign: 'center',
  verticalAlign: 'middle',
  boxSizing: 'border-box',
};

const thSelectionSpacerStyle = {
  width: 28,
  minWidth: 28,
  maxWidth: 28,
  border: 'none',
  background: 'transparent',
  padding: '0 0.2rem 0 0',
};

const tdBroughtForwardLabelStyle = {
  ...tdLeadStyle,
  textAlign: 'right',
  paddingRight: '0.4rem',
  paddingLeft: '0.2rem',
  fontWeight: 700,
  fontSize: '0.72rem',
  letterSpacing: '0.03em',
  background: '#f1f1f1',
};

const selectedFlightFeedbackStyle = {
  fontSize: '0.8rem',
};

const utcFieldErrorStyle = {
  color: '#a01818',
  fontSize: '0.62rem',
  lineHeight: 1.1,
  marginTop: '0.14rem',
};

const compactLogbookActionRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
  marginBottom: '0.55rem',
  flexWrap: 'wrap',
};

const deleteModalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1200,
};

const deleteModalCardStyle = {
  width: 'min(420px, calc(100vw - 2rem))',
  background: '#ffffff',
  border: '1px solid #cdcdcd',
  borderRadius: 8,
  padding: '0.85rem 0.9rem',
  boxShadow: '0 14px 32px rgba(0,0,0,0.2)',
};

const deleteModalHeadingStyle = {
  fontSize: '0.92rem',
  fontWeight: 700,
  color: '#151515',
  marginBottom: '0.35rem',
};

const deleteModalMessageStyle = {
  fontSize: '0.8rem',
  color: '#333333',
  lineHeight: 1.35,
};

const deleteModalActionsStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '0.5rem',
  marginTop: '0.8rem',
};

const deleteModalDeleteButtonStyle = {
  border: '1px solid #9a2a2a',
  background: '#fff5f5',
  color: '#8b1a1a',
  borderRadius: 6,
  padding: '0.28rem 0.6rem',
  fontSize: '0.76rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const deleteModalCancelButtonStyle = {
  border: '1px solid #808080',
  background: '#f3f3f3',
  color: '#2a2a2a',
  borderRadius: 6,
  padding: '0.28rem 0.6rem',
  fontSize: '0.76rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const logbookSettingsWrapStyle = {
  marginBottom: '0.75rem',
  width: 'fit-content',
  maxWidth: '100%',
};


const logbookSettingsPanelStyle = {
  marginTop: '0.45rem',
  border: '1px solid #cbcbcb',
  borderRadius: 6,
  background: '#f8f8f8',
  padding: '0.5rem 0.6rem',
  display: 'grid',
  gap: '0.28rem',
};

const logbookSettingsLabelStyle = {
  fontSize: '0.74rem',
  fontWeight: 700,
  color: '#1f1f1f',
};

const logbookSettingsOptionStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.35rem',
  fontSize: '0.74rem',
  color: '#1f1f1f',
};

const logbookSettingsFeedbackStyle = {
  fontSize: '0.72rem',
  marginTop: '0.1rem',
};
